/**
 * React should import only from `src/utils.js` (not this file).
 * Node backend uses `backend/utils/customUtils.js` (imports this `.mjs` module).
 *
 * Subscription status: admin UI + backend (`subscriptions.status`).
 * DB/API integers: 1 = subscribed, 2 = unsubscribed, 3 = never subscribed.
 * Legacy rows may still be NULL or 0 — UI treats those like "never".
 * Filter API: omit status, or send 0 / null / blank → "all" (no filter).
 */

/** Single source: string code → label (dropdown order: 1, 2, 3) */
const SUBSCRIPTION_STATUS_OPTIONS = Object.freeze({
  "1": "Subscribed",
  "2": "Unsubscribed",
  "3": "Never Subscribed",
});

let _codesCache = null;

/** @returns {{ neverSubscribed: number, subscribed: number, unsubscribed: number }} */
function subscriptionStatusCodes() {
  if (!_codesCache) {
    const subEntry = Object.entries(SUBSCRIPTION_STATUS_OPTIONS).find(([, l]) => l === "Subscribed");
    const unsubEntry = Object.entries(SUBSCRIPTION_STATUS_OPTIONS).find(([, l]) => l === "Unsubscribed");
    const neverEntry = Object.entries(SUBSCRIPTION_STATUS_OPTIONS).find(([, l]) => l === "Never Subscribed");
    _codesCache = Object.freeze({
      subscribed: subEntry ? Number(subEntry[0]) : 1,
      unsubscribed: unsubEntry ? Number(unsubEntry[0]) : 2,
      neverSubscribed: neverEntry ? Number(neverEntry[0]) : 3,
    });
  }
  return _codesCache;
}

/** Parse JSON string or pass through object; invalid/empty → fallback. */
function safeJsonParse(raw, fallback = null) {
  if (raw == null) return fallback;
  if (typeof raw === "object") return raw;
  const text = String(raw).trim();
  if (!text) return fallback;
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

/** `app_install` / `sign-up` → "App Install" / "Sign Up"; empty → "—". An event-metric funnel step
 *  (`metric:booking`) shows its metric name ("Booking"). */
function labelizeKey(value) {
  // A funnel step may hold several events joined by FUNNEL_STEP_SEPARATOR → "Signup or Login".
  const parts = String(value || "").split(FUNNEL_STEP_SEPARATOR).map((part) => {
    let s = part.trim();
    if (s.toLowerCase().startsWith(EVENT_METRIC_PREFIX)) s = s.slice(EVENT_METRIC_PREFIX.length);
    return s.replace(/[_\-.]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
  }).filter(Boolean);
  return parts.length ? parts.join(" or ") : "—";
}

/** Normalize support field names for matching (`Browser Name` → `browser_name`). */
function normalizeSupportFieldNameKey(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Read a scalar from support ticket context_json (top-level or fields[] by name). */
function getSupportContextScalar(contextOrRaw, nameKeys) {
  const parsed = safeJsonParse(contextOrRaw, {});
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return "";
  const wanted = new Set((nameKeys || []).map(normalizeSupportFieldNameKey));

  for (const key of nameKeys || []) {
    const v = parsed[key];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }

  if (Array.isArray(parsed.fields)) {
    for (const field of parsed.fields) {
      const nameKey = normalizeSupportFieldNameKey(field?.name || field?.id);
      if (!wanted.has(nameKey)) continue;
      const v = field?.value;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v).trim();
      }
    }
  }
  return "";
}

/**
 * UI: `label` + `badgeClass` — numeric `status` from API/DB (1|2|3); NULL / 0 / invalid → never.
 * Styles: `App.css` — `.badge.bg-light-*` (Bootstrap palette).
 */
function getSubscriptionDisplayStatus(code) {
  const { neverSubscribed, subscribed, unsubscribed } = subscriptionStatusCodes();
  const n = code == null || code === "" ? NaN : Number(code);

  if (n === unsubscribed) {
    return {
      label: SUBSCRIPTION_STATUS_OPTIONS[String(unsubscribed)],
      badgeClass: "badge rounded-pill bg-light-danger",
    };
  }
  if (n === subscribed) {
    return {
      label: SUBSCRIPTION_STATUS_OPTIONS[String(subscribed)],
      badgeClass: "badge rounded-pill bg-light-success",
    };
  }
  if (n === neverSubscribed) {
    return {
      label: SUBSCRIPTION_STATUS_OPTIONS[String(neverSubscribed)],
      badgeClass: "badge rounded-pill bg-light-secondary",
    };
  }
  return {
    label: "Unknown",
    badgeClass: null,
  };
}

/**
 * PH1 billing schema enums — all stored as INTs in DB.
 * Array shape: { value, label, badgeClass? } — lookup by int via findOptionByValue().
 *
 * Keep in sync with:
 * - backend/sql/query-migration.sql
 * - future PH12+ billing logic
 */
function findOptionByValue(list, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return list.find((x) => x.value === n) ?? null;
}

const METRIC_TYPE_CODES = Object.freeze([
  { value: 0, label: "Base" },
  { value: 1, label: "Mobile Push" },
  { value: 2, label: "Web Push" },
  { value: 3, label: "MMP Install" },
  { value: 4, label: "MMP Event" },
  { value: 5, label: "Support Agent" },
  // { value: 6, label: "WhatsApp" },
]);

/** Per-unit rate display (small decimal rates like 0.0048) — bigAmountFormat's 2-decimal
 *  rounding is wrong for these; this keeps up to 4 places, trimmed. */
function formatMetricPrice(symbol, price) {
  const sym = symbol || "";
  const n = parseFloat(price);
  if (!Number.isFinite(n)) return `${sym}0`;
  const text = Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/\.?0+$/, "");
  return `${sym}${text}`;
}

const BILLING_CURRENCY_CODES = Object.freeze([
  { value: 1, label: "INR", country_code: "IN", symbol: "₹", locale: "en-IN" },
  { value: 2, label: "GBP", country_code: "GB", symbol: "£", locale: "en-GB" },
  { value: 3, label: "USD", country_code: "US", symbol: "$", locale: "en-US" },
  // { value: 4, label: "EUR", country_code: "EU", symbol: "€", locale: "de-DE" },
]);

const DEFAULT_BILLING_CURRENCY = BILLING_CURRENCY_CODES[2];

const COMPACT_COUNT_SUFFIXES = Object.freeze(["K", "M", "B", "T", "Qa", "Qi"]);

/**
 * Counts compact (2.3K / 4.4M / 1.2B) when `currency` is omitted.
 * Amounts use that currency row’s locale (ISO, symbol, or catalog value).
 * @param {'decimal'} [format] numbers only; otherwise currency with symbol
 */
/** Exact count with thousands separators in the display locale: 2218 → "2,218" (no K / M rounding). */
function countFormat(n) {
  const num = Number(n);
  return new Intl.NumberFormat(DEFAULT_DISPLAY_LOCALE, { maximumFractionDigits: 0 }).format(Number.isFinite(num) ? num : 0);
}

function bigAmountFormat(n, currency, format) {
  const raw = currency == null || currency === "" ? "" : String(currency).trim();
  const upper = raw.toUpperCase();
  const asNum = Number(raw);
  const row = (raw && BILLING_CURRENCY_CODES.find((c) =>
    c.label === upper || c.symbol === raw || c.value === asNum
  )) || DEFAULT_BILLING_CURRENCY;
  const num = Number(n);
  const valid = n != null && n !== "" && Number.isFinite(num);

  let formatted;
  if (raw === "") {
    if (!valid) {
      formatted = "0";
    } else {
      const abs = Math.abs(num);
      let scaled = abs;
      let suffix = "";
      for (let i = 0; i < COMPACT_COUNT_SUFFIXES.length && scaled >= 1000; i += 1) {
        scaled /= 1000;
        suffix = COMPACT_COUNT_SUFFIXES[i];
      }
      if (suffix === "") {
        formatted = String(Math.round(num));
      } else {
        formatted = `${num < 0 ? "-" : ""}${scaled.toFixed(1).replace(/\.0$/, "")}${suffix}`;
      }
    }
  } else {
    const opts = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    if (format !== "decimal") {
      opts.style = "currency";
      opts.currency = row.label;
    }
    formatted = new Intl.NumberFormat(row.locale, opts).format(valid ? num : 0);
  }
  return formatted;
}

/**
 * user_subscriptions.status — change values/labels here only.
 * [0] Active — normal billing
 * [1] Backs mode — admin pause (no new invoices / auto-charge; API + dashboard stay on)
 * [2] Suspended — unpaid bill 30+ days (API + dashboard blocked until paid → Active)
 * [3] Cancelled — history only
 * [5] Upcoming — scheduled row; not the live plan until apply_at, then cron sets Active
 */
const BILLING_STATUS_CODES = Object.freeze([
  { value: 1, key: "active", label: "Active", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, key: "backs_mode", label: "Backs mode", badgeClass: "badge rounded-pill bg-light-warning" },
  { value: 3, key: "suspended", label: "Suspended", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 4, key: "cancelled", label: "Cancelled", badgeClass: "badge rounded-pill bg-light-danger" },
  // Prior plan row left behind after a Growth<->Enterprise switch (superseded, not user-cancelled).
  { value: 5, key: "switched", label: "Switched", badgeClass: "badge rounded-pill bg-light-info" },
  { value: 6, key: "upcoming", label: "Upcoming", badgeClass: "badge rounded-pill bg-light-primary" },
]);

/** Compare DB status to BILLING_STATUS_CODES[index].value (0 Active … 3 Cancelled). */
function isBillingStatus(status, index) {
  return Number(status) === BILLING_STATUS_CODES[index]?.value;
}

/** Current subscription row statuses. Cancelled, Switched, and Upcoming are not the live plan. */
function getCurrentBillingStatusValues() {
  const skip = ["cancelled", "switched", "upcoming"];
  return BILLING_STATUS_CODES.filter((x) => !skip.includes(x.key)).map((x) => x.value);
}

/** True for a prior-plan row left behind by a Growth<->Enterprise switch. */
function isSwitchedBillingStatus(status) {
  return findOptionByValue(BILLING_STATUS_CODES, status)?.key === "switched";
}

/**
 * RBI/Razorpay high-amount AFA threshold (kept for a future unlock flow).
 * Current live billing targets charges at or below this amount — unlock UI/API removed for now.
 */
const CARD_AFA_THRESHOLD_INR = 15000;
const HIGH_AFA_UNLOCK_AMOUNT = CARD_AFA_THRESHOLD_INR + 1;

const PAYMENT_MODE_CODES = Object.freeze([
  { value: 1, label: "Auto" },
  { value: 2, label: "Manual" },
]);

const PAYMENT_METHOD_CODES = Object.freeze([
  { value: 1, label: "Card" },
  { value: 2, label: "UPI" },
  { value: 3, label: "Bank" },
  { value: 4, label: "Razorpay" },
]);

/** Catalog plan category — stored as `plans.plan_type` (1=Free, 2=Growth, 3=Enterprise). */
const PLAN_TYPE_CODES = Object.freeze([
  { value: 1, label: "Free" },
  { value: 2, label: "Growth" },
  { value: 3, label: "Enterprise" },
]);

const INVOICE_STATUS_CODES = Object.freeze([
  { value: 0, label: "Pending", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 1, label: "Paid", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, label: "Failed", badgeClass: "badge rounded-pill bg-light-danger" },
  { value: 3, label: "Cancelled", badgeClass: "badge rounded-pill bg-light-warning" },
]);

/** `billing_invoices.invoice_kind` — 1=usage bill, 2=plan purchase (checkout). */
const INVOICE_KIND_CODES = Object.freeze([
  { value: 1, label: "Usage bill", badgeClass: "badge rounded-pill bg-light-info" },
  { value: 2, label: "Plan purchase", badgeClass: "badge rounded-pill bg-light-primary" },
]);

/** Invoice row is plan-purchase / checkout (`invoice_kind` = Plan purchase). */
function isCheckoutInvoice(invoice) {
  return Number(invoice?.invoice_kind) === INVOICE_KIND_CODES[1].value;
}

const USAGE_SOURCE_CODES = Object.freeze([
  { value: 1, label: "Subscriptions" },
  { value: 2, label: "Campaign Attributions" },
  { value: 3, label: "Notifications" },
  { value: 4, label: "App Support" },
  { value: 5, label: "Manual" },
]);

/** plan_change_requests.status — logged-in user upgrade / plan change approval queue */
const PLAN_CHANGE_REQUEST_STATUS_CODES = Object.freeze([
  { value: 1, label: "Pending", badgeClass: "badge rounded-pill bg-light-warning" },
  { value: 2, label: "Approved", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 3, label: "Rejected", badgeClass: "badge rounded-pill bg-light-danger" },
]);

function billingStatusLabel(code) {
  return findOptionByValue(BILLING_STATUS_CODES, code)?.label ?? BILLING_STATUS_CODES[0].label;
}

function getBillingStatusDisplay(value) {
  const entry = findOptionByValue(BILLING_STATUS_CODES, value);
  if (!entry) return { label: "—", badgeClass: null };
  return { label: entry.label, badgeClass: entry.badgeClass ?? null };
}

function getInvoiceStatusDisplay(value) {
  const entry = findOptionByValue(INVOICE_STATUS_CODES, value);
  if (!entry) return { label: "—", badgeClass: null };
  return { label: entry.label, badgeClass: entry.badgeClass ?? null };
}

function getInvoiceKindDisplay(value) {
  const entry = findOptionByValue(INVOICE_KIND_CODES, value);
  if (!entry) return { label: "—", badgeClass: null };
  return { label: entry.label, badgeClass: entry.badgeClass ?? null };
}

/** Parse `billing_invoices.usage_breakdown` JSON (object or string).
 *  Single source of truth — backend `customUtils.js` imports from here too. */
function parseInvoiceBreakdown(raw) {
  if (raw == null || raw === "") return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/** @deprecated Prefer `isCheckoutInvoice(invoice)` on row `invoice_kind`. Kept for UI that only has breakdown. */
function isCheckoutInvoiceBreakdown(breakdown) {
  if (!breakdown || typeof breakdown !== "object") return false;
  if (breakdown.checkout === true || breakdown.kind === "checkout") return true;
  if (breakdown.kind === "usage") return false;
  if (breakdown.base_amount != null && breakdown.plan_name != null && !breakdown.usageByType) {
    return true;
  }
  return false;
}

/** Metric rows from saved invoice snapshot (admin edit modal). */
function getInvoiceMetricRows(bd) {
  const usageByType = bd?.usageByType || {};
  const rates = Array.isArray(bd?.rates_applied) ? bd.rates_applied : [];
  const rateByType = Object.fromEntries(rates.map((r) => [Number(r.type), parseFloat(r.price) || 0]));
  return (METRIC_TYPE_CODES || []).filter((m) => m.value > 0).map((m) => {
    const count = Number(usageByType[m.value] ?? usageByType[String(m.value)] ?? 0);
    const rateRow = rates.find((r) => Number(r.type) === m.value);
    const rate = rateByType[m.value] ?? 0;
    return {
      type: m.value,
      label: rateRow?.type_label || m.label,
      count,
      rate,
      amount: count * rate,
    };
  });
}

/**
 * Charge-summary line rows — same structure as invoice PDF (`renderSimpleInvoicePdf`).
 */
function buildInvoiceChargeLineRows({ breakdown, planName, invoiceAmount, isCheckout }) {
  const bd = breakdown || {};
  const estimate = bd.estimate || {};
  const rates = Array.isArray(bd.rates_applied) ? bd.rates_applied : [];
  const usageByType = bd.usageByType || {};
  const rows = [];
  const hasItemized =
    rates.length > 0 ||
    parseFloat(estimate.base) > 0 ||
    Object.keys(usageByType || {}).length > 0;

  // Checkout without metric snapshot → single plan line; else same rows as usage bills.
  if (isCheckout && !hasItemized) {
    const amt = parseFloat(invoiceAmount) || 0;
    return [
      {
        title: `${planName || "Plan"} subscription`,
        qty: 1,
        rate: amt,
        amount: amt,
      },
    ];
  }

  const base = parseFloat(estimate.base) || 0;
  if (base > 0) {
    rows.push({
      title: `${planName || "Plan"} base subscription`,
      qty: 1,
      rate: base,
      amount: base,
    });
  }

  if (rates.length) {
    for (const row of rates) {
      const type = Number(row.type);
      if (!type) continue;
      const qty = Number(usageByType[type] ?? usageByType[String(type)] ?? 0);
      const rate = parseFloat(row.price) || 0;
      rows.push({
        title: row.type_label || `Metric ${type}`,
        qty,
        rate,
        amount: qty * rate,
      });
    }
  } else {
    for (const m of METRIC_TYPE_CODES || []) {
      if (!(m.value > 0)) continue;
      const qty = Number(usageByType[m.value] ?? usageByType[String(m.value)] ?? 0);
      if (!qty && isCheckout) continue;
      rows.push({
        title: m.label,
        qty,
        rate: 0,
        amount: 0,
      });
    }
  }

  if (!rows.length && isCheckout) {
    const amt = parseFloat(invoiceAmount) || 0;
    rows.push({
      title: `${planName || "Plan"} subscription`,
      qty: 1,
      rate: amt,
      amount: amt,
    });
  }

  return rows;
}

/** Tax snapshot for modal / PDF totals — `grand_total` matches `invoice.amount`. */
function resolveInvoiceTaxSnapshot(breakdown, invoiceAmount) {
  const bd = breakdown || {};
  const estimate = bd.estimate || {};
  const subtotal =
    Number(bd.subtotal_before_tax ?? estimate.subtotal_before_tax ?? estimate.total) ||
    Math.round(((parseFloat(estimate.base) || 0) + (parseFloat(estimate.variable) || 0)) * 100) / 100;

  const gstPercent = Number(bd.gst_percent ?? estimate.gst_percent) || 0;
  let gstAmount = Number(bd.gst_amount ?? estimate.gst_amount);
  if (!Number.isFinite(gstAmount) && gstPercent > 0) {
    gstAmount = Math.round(subtotal * gstPercent) / 100;
    gstAmount = Math.round(gstAmount * 100) / 100;
  } else if (!Number.isFinite(gstAmount)) {
    gstAmount = 0;
  }

  const amountNum = Number(invoiceAmount);
  let grandTotal = Number(estimate.grand_total ?? bd.grand_total);
  if (!Number.isFinite(grandTotal) || grandTotal <= 0) {
    grandTotal =
      Number.isFinite(amountNum) && amountNum > 0
        ? amountNum
        : Math.round((subtotal + gstAmount) * 100) / 100;
  }

  return { subtotal_before_tax: subtotal, gst_percent: gstPercent, gst_amount: gstAmount, grand_total: grandTotal };
}

function getPlanChangeRequestStatusDisplay(value) {
  const entry = findOptionByValue(PLAN_CHANGE_REQUEST_STATUS_CODES, value);
  if (!entry) return { label: "—", badgeClass: null };
  return { label: entry.label, badgeClass: entry.badgeClass ?? null };
}

/**
 * Generic slug helper for any entity (plans, roles, codes, …).
 * @param {string} text
 * @param {{ separator?: string, maxLength?: number }} [options]
 */
function slugify(text, options = {}) {
  const separator = options.separator != null ? String(options.separator) : "-";
  const maxLength = Number.isFinite(options.maxLength) ? options.maxLength : 120;
  const escaped = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, separator)
    .replace(new RegExp(`^${escaped}+|${escaped}+$`, "g"), "")
    .slice(0, maxLength);
}

/** Role taxonomy — /admin = superadmin panel; dashboard = platform admin. */
const ROLE_TYPES = Object.freeze({
  SUPERADMIN: "superadmin",
  SUPERADMIN_TEAM: "superadmin_team",
  ADMIN: "admin",
  ADMIN_TEAM: "admin_team",
});

const PERMISSION_SCOPES = Object.freeze({
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
});

/** Default system / per-admin team role slug (`admin_team`). Per-admin row uses same slug with owner_user_id set. */
const TEAM_MEMBER_ROLE_SLUG = "support_team_member";

/**
 * Staff user = assigned role type is `admin_team`.
 * Expects `user.role_type` (set at sign-in / on `req` from verifyToken).
 */
function isStaffUser(user) {
  if (!user) return false;
  return String(user.role_type || "") === ROLE_TYPES.ADMIN_TEAM;
}

/**
 * Account owner / superadmin roles have implicit full access (no permission rows).
 * Team roles (`admin_team` / `superadmin_team`) must have explicit permission keys.
 */
function roleHasUnrestrictedAccess(roleType) {
  const t = String(roleType || "");
  return t === ROLE_TYPES.ADMIN || t === ROLE_TYPES.SUPERADMIN;
}

/**
 * Laravel-style ability check: `can(user, 'team.view')`.
 * `user` may be `{ role_type, permissions: string[] }` (API/localStorage),
 * `{ roleType, permissionKeys }` (req), or a plain string[] of keys.
 * Unrestricted roles (admin / superadmin) always return true.
 */
function can(userOrKeys, permission) {
  const key = String(permission || "").trim();
  if (!key) return false;
  // Always allow apps list so /apps can bootstrap role+permission seed
  // even when roles/permissions were emptied (orphan role_id → empty keys).
  if (key === "apps.view") return true;

  if (Array.isArray(userOrKeys)) {
    return userOrKeys.includes(key) || userOrKeys.includes("*");
  }

  if (!userOrKeys || typeof userOrKeys !== "object") return false;

  const roleType = userOrKeys.role_type || userOrKeys.roleType || null;
  if (roleHasUnrestrictedAccess(roleType)) return true;

  const keys = userOrKeys.permissions || userOrKeys.permissionKeys || userOrKeys.abilities || [];
  if (!Array.isArray(keys) || !keys.length) return false;
  if (keys.includes("*")) return true;
  return keys.includes(key);
}

/** True if the user can perform any of the given permissions. */
function canAny(userOrKeys, permissions) {
  if (!Array.isArray(permissions) || !permissions.length) return false;
  return permissions.some((p) => can(userOrKeys, p));
}

/** True if the user can perform all of the given permissions. */
function canAll(userOrKeys, permissions) {
  if (!Array.isArray(permissions) || !permissions.length) return false;
  return permissions.every((p) => can(userOrKeys, p));
}

/** Permissions for /admin (superadmin) panel. */
const SUPERADMIN_PERMISSIONS = Object.freeze([
  { key: "sa.dashboard.view", name: "View Dashboard", module: "Dashboard", description: "Access superadmin dashboard" },
  { key: "sa.users.view", name: "View Platform Users", module: "Users", description: "List and view platform users" },
  { key: "sa.users.manage", name: "Manage Platform Users", module: "Users", description: "Edit or delete platform users" },
  { key: "sa.applications.view", name: "View Applications", module: "Applications", description: "List applications and app users" },
  { key: "sa.applications.manage", name: "Manage Applications", module: "Applications", description: "Manage applications" },
  { key: "sa.plans.view", name: "View Plans", module: "Plans", description: "View plans" },
  { key: "sa.plans.add", name: "Add Plans", module: "Plans", description: "Add plans" },
  { key: "sa.plans.edit", name: "Edit Plans", module: "Plans", description: "Edit plans" },
  { key: "sa.plans.delete", name: "Delete Plans", module: "Plans", description: "Delete plans" },
  { key: "sa.plans.change_status", name: "Change Status Plans", module: "Plans", description: "Change plan status" },
  { key: "sa.billing.view", name: "View Billing", module: "Billing", description: "View billing and invoices" },
  { key: "sa.billing.manage", name: "Manage Billing", module: "Billing", description: "Manage billing operations" },
  { key: "sa.pages.view", name: "View Pages", module: "Pages", description: "View CMS pages" },
  { key: "sa.pages.manage", name: "Manage Pages", module: "Pages", description: "Edit CMS pages" },
  { key: "sa.settings.view", name: "View Settings", module: "Settings", description: "View superadmin settings" },
  { key: "sa.settings.manage", name: "Manage Settings", module: "Settings", description: "Update superadmin settings" },
  { key: "sa.support.view", name: "View Support", module: "Support", description: "View platform support chats" },
  { key: "sa.support.manage", name: "Manage Support", module: "Support", description: "Reply and manage support" },
  { key: "sa.registration.view", name: "View Registration Requests", module: "Registration", description: "View signup requests" },
  { key: "sa.registration.manage", name: "Manage Registration Requests", module: "Registration", description: "Approve or reject signups" },
  { key: "sa.roles.view", name: "View Roles", module: "Roles", description: "View roles" },
  { key: "sa.roles.add", name: "Add Roles", module: "Roles", description: "Add roles" },
  { key: "sa.roles.edit", name: "Edit Roles", module: "Roles", description: "Edit roles" },
  { key: "sa.roles.delete", name: "Delete Roles", module: "Roles", description: "Delete roles" },
  { key: "sa.roles.change_status", name: "Change Status Roles", module: "Roles", description: "Change role status" },
]);

/** Permissions for platform admin dashboard (team roles).
 * Hierarchy (single source): section → menu (accordion) → module (box) → permission.
 * Array order is the UI order (section → menu → module → actions).
 * `name` is the short action label shown in the Roles UI.
 */
const ADMIN_PERMISSIONS = Object.freeze([
  // ── Main Menu ──
  { key: "apps.view", name: "View", section: "Main Menu", menu: "Apps", module: "Apps", description: "View apps" },
  { key: "apps.add", name: "Add", section: "Main Menu", menu: "Apps", module: "Apps", description: "Add apps" },
  { key: "apps.edit", name: "Edit", section: "Main Menu", menu: "Apps", module: "Apps", description: "Edit apps" },
  { key: "apps.change_status", name: "Change Status", section: "Main Menu", menu: "Apps", module: "Apps", description: "Change app status" },
  { key: "apps.delete", name: "Delete", section: "Main Menu", menu: "Apps", module: "Apps", description: "Delete apps" },

  { key: "plan.view", name: "View", section: "Main Menu", menu: "My Plan", module: "My Plan", description: "View plan details" },

  { key: "roles.view", name: "View", section: "Main Menu", menu: "Roles and Staff", module: "Roles and Permissions", description: "View roles" },
  { key: "roles.add", name: "Add", section: "Main Menu", menu: "Roles and Staff", module: "Roles and Permissions", description: "Add roles" },
  { key: "roles.edit", name: "Edit", section: "Main Menu", menu: "Roles and Staff", module: "Roles and Permissions", description: "Edit roles" },
  { key: "roles.change_status", name: "Change Status", section: "Main Menu", menu: "Roles and Staff", module: "Roles and Permissions", description: "Change role status" },
  { key: "roles.delete", name: "Delete", section: "Main Menu", menu: "Roles and Staff", module: "Roles and Permissions", description: "Delete roles" },
  { key: "team.view", name: "View", section: "Main Menu", menu: "Roles and Staff", module: "Staff", description: "View staff" },
  { key: "team.add", name: "Add", section: "Main Menu", menu: "Roles and Staff", module: "Staff", description: "Add staff" },
  { key: "team.edit", name: "Edit", section: "Main Menu", menu: "Roles and Staff", module: "Staff", description: "Edit staff" },
  { key: "team.change_status", name: "Change Status", section: "Main Menu", menu: "Roles and Staff", module: "Staff", description: "Change staff status" },
  { key: "team.delete", name: "Delete", section: "Main Menu", menu: "Roles and Staff", module: "Staff", description: "Delete staff" },

  { key: "billing.view", name: "View", section: "Main Menu", menu: "Billing", module: "Billing", description: "View billing and invoices" },
  { key: "billing.manage", name: "Manage", section: "Main Menu", menu: "Billing", module: "Billing", description: "Cancel subscription, upgrade or change plan" },

  { key: "sites.view", name: "View", section: "Main Menu", menu: "SSL Domain Check", module: "SSL Domain Check", description: "View sites" },
  { key: "sites.add", name: "Add", section: "Main Menu", menu: "SSL Domain Check", module: "SSL Domain Check", description: "Add sites" },
  { key: "sites.edit", name: "Edit", section: "Main Menu", menu: "SSL Domain Check", module: "SSL Domain Check", description: "Edit sites" },
  { key: "sites.change_status", name: "Change Status", section: "Main Menu", menu: "SSL Domain Check", module: "SSL Domain Check", description: "Change site status" },
  { key: "sites.delete", name: "Delete", section: "Main Menu", menu: "SSL Domain Check", module: "SSL Domain Check", description: "Delete sites" },
  { key: "sites.notification_settings.view", name: "View", section: "Main Menu", menu: "SSL Domain Check", module: "Notification settings", description: "View SSL notification settings" },
  { key: "sites.notification_settings.manage", name: "Manage", section: "Main Menu", menu: "SSL Domain Check", module: "Notification settings", description: "Manage SSL notification settings" },

  // ── App Menu ──
  { key: "push.view", name: "View", section: "App Menu", menu: "Messages", module: "Push", description: "View push notifications" },
  { key: "push.manage", name: "Manage", section: "App Menu", menu: "Messages", module: "Push", description: "Send and manage push" },

  { key: "segments.view", name: "View", section: "App Menu", menu: "Audience", module: "Segments", description: "View segments" },
  { key: "segments.add", name: "Add", section: "App Menu", menu: "Audience", module: "Segments", description: "Add segments" },
  { key: "segments.edit", name: "Edit", section: "App Menu", menu: "Audience", module: "Segments", description: "Edit segments" },
  { key: "segments.delete", name: "Delete", section: "App Menu", menu: "Audience", module: "Segments", description: "Delete segments" },
  { key: "app_users.view", name: "View", section: "App Menu", menu: "Audience", module: "Users", description: "View end users" },
  { key: "app_users.delete", name: "Delete", section: "App Menu", menu: "Audience", module: "Users", description: "Delete end users" },
  { key: "subscriptions.view", name: "View", section: "App Menu", menu: "Audience", module: "Subscriptions", description: "View subscriptions" },
  { key: "subscriptions.edit", name: "Edit", section: "App Menu", menu: "Audience", module: "Subscriptions", description: "Edit subscriptions" },
  { key: "subscriptions.delete", name: "Delete", section: "App Menu", menu: "Audience", module: "Subscriptions", description: "Delete subscriptions" },
  { key: "test_subscriptions.view", name: "View", section: "App Menu", menu: "Audience", module: "Test Subscription", description: "View test subscriptions" },
  { key: "test_subscriptions.delete", name: "Delete", section: "App Menu", menu: "Audience", module: "Test Subscription", description: "Delete test subscriptions" },

  { key: "whatsapp.inbox.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Inbox", description: "View WhatsApp inbox" },
  { key: "whatsapp.inbox.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Inbox", description: "Reply in WhatsApp inbox" },
  { key: "whatsapp.contacts.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Contacts", description: "View WhatsApp contacts" },
  { key: "whatsapp.campaigns.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Campaigns", description: "View WhatsApp campaigns" },
  { key: "whatsapp.campaigns.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Campaigns", description: "Manage WhatsApp campaigns" },
  { key: "whatsapp.analytics.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Analytics", description: "View WhatsApp analytics" },
  { key: "whatsapp.business.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Business profile", description: "View business profile" },
  { key: "whatsapp.business.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Business profile", description: "Manage business profile" },
  { key: "whatsapp.phones.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Phone numbers", description: "View phone numbers" },
  { key: "whatsapp.connection.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Connection", description: "View WhatsApp connection" },
  { key: "whatsapp.connection.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Connection", description: "Manage WhatsApp connection" },
  { key: "whatsapp.test.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Test template", description: "View test template" },
  { key: "whatsapp.test.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Test template", description: "Send test templates" },
  { key: "whatsapp.templates.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Templates", description: "View WhatsApp templates" },
  { key: "whatsapp.templates.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Templates", description: "Manage WhatsApp templates" },
  { key: "whatsapp.send.view", name: "View", section: "App Menu", menu: "WhatsApp", module: "Send Message", description: "View send message" },
  { key: "whatsapp.send.manage", name: "Manage", section: "App Menu", menu: "WhatsApp", module: "Send Message", description: "Send WhatsApp messages" },

  { key: "campaigns.view", name: "View", section: "App Menu", menu: "Engage", module: "Campaign", description: "View engage campaigns" },
  { key: "onelink.view", name: "View", section: "App Menu", menu: "Engage", module: "OneLink", description: "View OneLinks" },
  { key: "onelink.add", name: "Add", section: "App Menu", menu: "Engage", module: "OneLink", description: "Add OneLinks" },
  { key: "onelink.edit", name: "Edit", section: "App Menu", menu: "Engage", module: "OneLink", description: "Edit OneLinks" },
  { key: "onelink.delete", name: "Delete", section: "App Menu", menu: "Engage", module: "OneLink", description: "Delete OneLinks" },
  { key: "events.view", name: "View", section: "App Menu", menu: "Engage", module: "Events", description: "View attribution events" },
  { key: "funnel.view", name: "View", section: "App Menu", menu: "Engage", module: "Funnel", description: "View saved funnels and their reports" },
  { key: "funnel.manage", name: "Manage", section: "App Menu", menu: "Engage", module: "Funnel", description: "Create and update funnels" },
  { key: "funnel.delete", name: "Delete", section: "App Menu", menu: "Engage", module: "Funnel", description: "Delete funnels" },

  { key: "collaborate.partner_integrations.view", name: "View", section: "App Menu", menu: "Collaborate", module: "Partner Integrations", description: "View partner integrations" },
  { key: "collaborate.partner_integrations.manage", name: "Manage", section: "App Menu", menu: "Collaborate", module: "Partner Integrations", description: "Manage partner integrations" },

  { key: "settings.platform.view", name: "View", section: "App Menu", menu: "Setting", module: "Platform", description: "View platform settings" },
  { key: "settings.platform.manage", name: "Manage", section: "App Menu", menu: "Setting", module: "Platform", description: "Manage platform settings" },
  { key: "settings.messaging.group.view", name: "View", section: "App Menu", menu: "Notification Channel", module: "Group", description: "View notification groups" },
  { key: "settings.messaging.group.add", name: "Add", section: "App Menu", menu: "Notification Channel", module: "Group", description: "Add notification groups" },
  { key: "settings.messaging.group.edit", name: "Edit", section: "App Menu", menu: "Notification Channel", module: "Group", description: "Edit notification groups" },
  { key: "settings.messaging.group.delete", name: "Delete", section: "App Menu", menu: "Notification Channel", module: "Group", description: "Delete notification groups" },
  { key: "settings.messaging.channel.view", name: "View", section: "App Menu", menu: "Notification Channel", module: "Channel", description: "View notification channels" },
  { key: "settings.messaging.channel.add", name: "Add", section: "App Menu", menu: "Notification Channel", module: "Channel", description: "Add notification channels" },
  { key: "settings.messaging.channel.edit", name: "Edit", section: "App Menu", menu: "Notification Channel", module: "Channel", description: "Edit notification channels" },
  { key: "settings.messaging.channel.delete", name: "Delete", section: "App Menu", menu: "Notification Channel", module: "Channel", description: "Delete notification channels" },
  { key: "settings.api_keys.view", name: "View", section: "App Menu", menu: "Setting", module: "API Keys", description: "View API keys" },
  { key: "settings.api_keys.add", name: "Add", section: "App Menu", menu: "Setting", module: "API Keys", description: "Add API keys" },
  { key: "settings.api_keys.change_status", name: "Change Status", section: "App Menu", menu: "Setting", module: "API Keys", description: "Activate or deactivate API keys" },
  { key: "settings.api_keys.delete", name: "Delete", section: "App Menu", menu: "Setting", module: "API Keys", description: "Delete API keys" },

  { key: "delivery.sent.view", name: "View", section: "App Menu", menu: "Delivery", module: "Sent Messages", description: "View sent messages" },
  { key: "delivery.scheduled.view", name: "View", section: "App Menu", menu: "Delivery", module: "Scheduled Messages", description: "View scheduled messages" },

  { key: "support.tickets.view", name: "View", section: "App Menu", menu: "Customer Support", module: "Tickets", description: "View support tickets" },
  { key: "support.tickets.manage", name: "Manage", section: "App Menu", menu: "Customer Support", module: "Tickets", description: "Reply and manage tickets" },
  { key: "support.team_members.view", name: "View", section: "App Menu", menu: "Customer Support", module: "Team Members", description: "View support team members" },
  { key: "support.team_members.add", name: "Add", section: "App Menu", menu: "Customer Support", module: "Team Members", description: "Add support team members" },
  { key: "support.team_members.edit", name: "Edit", section: "App Menu", menu: "Customer Support", module: "Team Members", description: "Edit support team members" },
  { key: "support.team_members.change_status", name: "Change Status", section: "App Menu", menu: "Customer Support", module: "Team Members", description: "Change team member status" },
  { key: "support.team_members.delete", name: "Delete", section: "App Menu", menu: "Customer Support", module: "Team Members", description: "Delete support team members" },
  { key: "support.customers.view", name: "View", section: "App Menu", menu: "Customer Support", module: "Customers", description: "View support customers" },
  { key: "support.customers.add", name: "Add", section: "App Menu", menu: "Customer Support", module: "Customers", description: "Add support customers" },
  { key: "support.customers.edit", name: "Edit", section: "App Menu", menu: "Customer Support", module: "Customers", description: "Edit support customers" },
  { key: "support.customers.change_status", name: "Change Status", section: "App Menu", menu: "Customer Support", module: "Customers", description: "Change customer status" },
  { key: "support.customers.delete", name: "Delete", section: "App Menu", menu: "Customer Support", module: "Customers", description: "Delete support customers" },
  { key: "support.groups.view", name: "View", section: "App Menu", menu: "Customer Support", module: "Groups", description: "View support groups" },
  { key: "support.groups.add", name: "Add", section: "App Menu", menu: "Customer Support", module: "Groups", description: "Add support groups" },
  { key: "support.groups.edit", name: "Edit", section: "App Menu", menu: "Customer Support", module: "Groups", description: "Edit support groups" },
  { key: "support.groups.change_status", name: "Change Status", section: "App Menu", menu: "Customer Support", module: "Groups", description: "Change group status" },
  { key: "support.groups.delete", name: "Delete", section: "App Menu", menu: "Customer Support", module: "Groups", description: "Delete support groups" },
  { key: "support.fields.view", name: "View", section: "App Menu", menu: "Customer Support", module: "Fields", description: "View support fields" },
  { key: "support.fields.add", name: "Add", section: "App Menu", menu: "Customer Support", module: "Fields", description: "Add support fields" },
  { key: "support.fields.edit", name: "Edit", section: "App Menu", menu: "Customer Support", module: "Fields", description: "Edit support fields" },
  { key: "support.fields.change_status", name: "Change Status", section: "App Menu", menu: "Customer Support", module: "Fields", description: "Change field status" },
  { key: "support.fields.delete", name: "Delete", section: "App Menu", menu: "Customer Support", module: "Fields", description: "Delete support fields" },
  { key: "automation.view", name: "View", section: "App Menu", menu: "Automations", module: "Automations", description: "View automations" },
  { key: "automation.add", name: "Add", section: "App Menu", menu: "Automations", module: "Automations", description: "Create new automations" },
  { key: "automation.edit", name: "Edit", section: "App Menu", menu: "Automations", module: "Automations", description: "Edit automation settings and flow" },
  { key: "automation.change_status", name: "Change Status", section: "App Menu", menu: "Automations", module: "Automations", description: "Activate, pause, or archive automations" },
  { key: "automation.delete", name: "Delete", section: "App Menu", menu: "Automations", module: "Automations", description: "Delete draft automations" },
]);

/**
 * Group API permission rows into section → menu (accordion) → module (box).
 * Order follows ADMIN_PERMISSIONS array order (no separate sort map / DB order column).
 */
function groupAdminPermissionsForUi(apiPermissions = []) {
  const metaByKey = new Map(ADMIN_PERMISSIONS.map((p) => [p.key, p]));
  const keyOrder = new Map(ADMIN_PERMISSIONS.map((p, i) => [p.key, i]));
  const sectionOrder = [];
  const menuOrderBySection = new Map();
  const moduleOrderByMenu = new Map();

  for (const def of ADMIN_PERMISSIONS) {
    if (!sectionOrder.includes(def.section)) sectionOrder.push(def.section);
    if (!menuOrderBySection.has(def.section)) menuOrderBySection.set(def.section, []);
    const menus = menuOrderBySection.get(def.section);
    if (!menus.includes(def.menu)) menus.push(def.menu);
    const menuKey = `${def.section}\0${def.menu}`;
    if (!moduleOrderByMenu.has(menuKey)) moduleOrderByMenu.set(menuKey, []);
    const modules = moduleOrderByMenu.get(menuKey);
    if (!modules.includes(def.module)) modules.push(def.module);
  }

  const buckets = new Map();
  for (const p of apiPermissions) {
    const meta = metaByKey.get(p.key);
    const section = meta?.section || "Other";
    const menu = meta?.menu || p.module || "Other";
    const module = meta?.module || p.module || "Other";
    if (!sectionOrder.includes(section)) sectionOrder.push(section);
    if (!menuOrderBySection.has(section)) menuOrderBySection.set(section, []);
    if (!menuOrderBySection.get(section).includes(menu)) menuOrderBySection.get(section).push(menu);
    const menuKey = `${section}\0${menu}`;
    if (!moduleOrderByMenu.has(menuKey)) moduleOrderByMenu.set(menuKey, []);
    if (!moduleOrderByMenu.get(menuKey).includes(module)) moduleOrderByMenu.get(menuKey).push(module);
    const bucketKey = `${section}\0${menu}\0${module}`;
    if (!buckets.has(bucketKey)) buckets.set(bucketKey, []);
    buckets.get(bucketKey).push(p);
  }

  const orderByAdminDefinition = (list) =>
    [...(list || [])].sort((a, b) => {
      const ai = keyOrder.has(a.key) ? keyOrder.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bi = keyOrder.has(b.key) ? keyOrder.get(b.key) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

  return sectionOrder
    .map((section) => ({
      section,
      menus: (menuOrderBySection.get(section) || [])
        .map((menu) => ({
          menu,
          modules: (moduleOrderByMenu.get(`${section}\0${menu}`) || [])
            .map((module) => ({
              module,
              list: orderByAdminDefinition(buckets.get(`${section}\0${menu}\0${module}`)),
            }))
            .filter((m) => m.list.length > 0),
        }))
        .filter((m) => m.modules.length > 0),
    }))
    .filter((s) => s.menus.length > 0);
}

/** Fixed system roles (seeded). Superadmin / Admin = full panel access (no permission assignment).
 * Support Team Member is defined here for per-owner seeding only — never created with owner_user_id NULL.
 */
const SYSTEM_ROLES = Object.freeze([
  {
    name: "Super Admin",
    slug: "superadmin",
    type: ROLE_TYPES.SUPERADMIN,
    is_system: 1,
  },
  {
    name: "Admin",
    slug: "admin",
    type: ROLE_TYPES.ADMIN,
    is_system: 1,
  },
  {
    name: "Support Team Member",
    slug: TEAM_MEMBER_ROLE_SLUG,
    type: ROLE_TYPES.ADMIN_TEAM,
    is_system: 1,
  },
]);

function getSystemRoleSlugs() {
  return SYSTEM_ROLES.map((r) => r.slug);
}

/** Permission keys under Customer Support menu (always locked on support_team_member). */
function getCustomerSupportPermissionKeys() {
  return ADMIN_PERMISSIONS.filter((p) => p.menu === "Customer Support").map((p) => p.key);
}

/** Annual billing: pay for 10 months (20% off ≈ 2 months free). */
const ANNUAL_BILLING_DISCOUNT_FACTOR = 0.8;

function getCurrencyObject(req) {
  const { country_code } = parseRequestHeader(req);
  const countryCode = country_code ? String(country_code).trim().toUpperCase() : null;

  let currency = DEFAULT_BILLING_CURRENCY;
  if (countryCode) {
    for (const c of BILLING_CURRENCY_CODES) {
      const codes = c.country_code;
      if (Array.isArray(codes) ? codes.includes(countryCode) : codes === countryCode) {
        currency = c;
        break;
      }
    }
  }

  return { ...currency, country_code: countryCode };
}

/**
 * Growth plan calculator — base (type 0) + per-metric variable usage.
 * Pass `freeLimits` only for the free tier (usage above limits is charged).
 * Paid plans pass an empty array so full usage is billed.
 * @param {Object} usageByType — e.g. { 1: mobilePushUnits, 2: webPushUnits, ... }
 */
function calculateGrowthPlanEstimate({
  pricing = [],
  freeLimits = [],
  usageByType = {},
  billingCycle = "monthly",
}) {
  const rateByType = Object.fromEntries(
    (pricing || []).map((row) => [Number(row.type), parseFloat(row.price) || 0])
  );
  const limitByType = Object.fromEntries(
    (freeLimits || []).map((row) => [Number(row.type), Math.max(0, parseInt(row.limit_value, 10) || 0)])
  );
  const applyFreeLimits = (freeLimits || []).length > 0;

  const base = rateByType[0] || 0;
  let variable = 0;

  const billableMetricTypes = METRIC_TYPE_CODES.filter((m) => m.value > 0).map((m) => m.value);
  for (const type of billableMetricTypes) {
    const usage = Math.max(0, Number(usageByType[type]) || 0);
    const billableUnits = applyFreeLimits
      ? Math.max(0, usage - (limitByType[type] || 0))
      : usage;
    variable += billableUnits * (rateByType[type] || 0);
  }

  let monthly = base + variable;
  if (billingCycle === "annual") {
    monthly = monthly * 12 * ANNUAL_BILLING_DISCOUNT_FACTOR;
  }

  return {
    base,
    variable,
    total: monthly,
    monthlyEquivalent: billingCycle === "annual" ? monthly / 12 : monthly,
  };
}

/** Request context: IP/UA/geo from headers; device fields from `/users` body when present. */
function parseRequestHeader(req, body = {}) {
  const headers = req?.headers || {};
  const user =
    body?.user && typeof body.user === "object" && !Array.isArray(body.user) ? body.user : {};

  let ip_address = null;
  const forwarded = headers["x-forwarded-for"];
  if (forwarded) {
    const first = String(forwarded).split(",")[0].trim();
    if (first) ip_address = first;
  }
  if (!ip_address) ip_address = req?.ip || req?.connection?.remoteAddress || null;

  const user_agent = headers["user-agent"] || "";

  let os_name = user.device_os ? String(user.device_os).trim() : null;
  let browser_name = user.browser_name ? String(user.browser_name).trim() : null;
  let device_type = null;

  if (os_name) {
    const osLower = os_name.toLowerCase();
    if (osLower.includes("android")) device_type = "android";
    else if (/(ios|iphone|ipad)/i.test(os_name)) device_type = "ios";
  }

  const ua = String(user_agent || "");
  const uaLower = ua.toLowerCase();
  if (!device_type) {
    if (/android/.test(uaLower)) device_type = "android";
    else if (/iphone|ipad|ipod/.test(uaLower)) device_type = "ios";
    else if (ua) device_type = "web";
    else device_type = "unknown";
  }
  if (!os_name) {
    if (device_type === "android") os_name = "Android";
    else if (device_type === "ios") os_name = "iOS";
    else if (ua) {
      if (/windows/.test(uaLower)) os_name = "Windows";
      else if (/mac os|macintosh/.test(uaLower)) os_name = "macOS";
      else if (/linux/.test(uaLower)) os_name = "Linux";
    }
  }
  if (!browser_name && ua) {
    if (/chrome\//.test(uaLower) && !/edg\//.test(uaLower)) browser_name = "Chrome";
    else if (/safari\//.test(uaLower) && !/chrome\//.test(uaLower)) browser_name = "Safari";
    else if (/firefox\//.test(uaLower)) browser_name = "Firefox";
    else if (/edg\//.test(uaLower)) browser_name = "Edge";
  }

  let country_code = null;
  // CDN/proxy in production; browser sends x-billing-country after geolocation.
  const geo = headers["x-billing-country"] || headers["x-appengine-country"] || headers["cloudfront-viewer-country"] || headers["cf-ipcountry"];
  if (geo && String(geo).length === 2) country_code = String(geo).toUpperCase();


  const cityHeader = headers["cf-ipcity"] || headers["x-appengine-city"] || headers["x-city"];
  const city = cityHeader ? String(cityHeader).trim().slice(0, 120) : null;

  // State/province (e.g. Maharashtra, CA) — analytics only, not billing.
  const regionHeader =
    headers["cf-region"] ||
    headers["cf-region-code"] ||
    headers["x-appengine-region"] ||
    headers["x-region"];
  const region = regionHeader ? String(regionHeader).trim().slice(0, 120) : null;

  return {
    ip_address,
    user_agent,
    device_type,
    os_name,
    browser_name,
    country_code,
    city,
    region,
  };
}


/** automations.status */
const AUTOMATION_STATUS_CODES = Object.freeze([
  { value: 1, key: "draft", label: "Draft", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 2, key: "scheduled", label: "Scheduled", badgeClass: "badge rounded-pill bg-light-info" },
  { value: 3, key: "live", label: "Live", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 4, key: "paused", label: "Paused", badgeClass: "badge rounded-pill bg-light-warning" },
  { value: 5, key: "ended", label: "Ended", badgeClass: "badge rounded-pill bg-light-dark" },
  { value: 6, key: "archived", label: "Archived", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

/** automations.template — 0 = scratch */
const AUTOMATION_TEMPLATE_CODES = Object.freeze([
  { value: 0, label: "Start from scratch" },
  { value: 1, label: "Onboarding" },
  { value: 2, label: "Promotional" },
  { value: 3, label: "Re-engagement" },
]);

/** automations.entry_type */
const AUTOMATION_ENTRY_TYPE_CODES = Object.freeze([
  { value: 1, key: "segment", label: "Audience Segment" },
  { value: 2, key: "custom_event", label: "Custom Event" },
]);

/** automations.exit_type */
const AUTOMATION_EXIT_TYPE_CODES = Object.freeze([
  { value: 1, key: "completed", label: "Completed automation" },
  { value: 2, key: "condition", label: "Meet condition" },
]);

/** automations.exit_conditions — bitmask, multiple can apply together. `tooltip` bataata h ki
    is option ke aage help-icon dikhana h ya nahi (Automation Builder ke Exit Rules tab me use hota h). */
const AUTOMATION_EXIT_CONDITION_BITS = Object.freeze([
  { value: 1, label: "Exit when a user becomes active in your app/website", tooltip: true },
  { value: 2, label: "Exit when custom event condition occurs", tooltip: false },
  { value: 4, label: "Exit when a user no longer matches the audience conditions", tooltip: true },
  { value: 8, label: "Exit when a user enters a segment", tooltip: true },
]);

/** automations.reentry_type */
const AUTOMATION_REENTRY_TYPE_CODES = Object.freeze([
  { value: 1, label: "Once only" },
  { value: 2, label: "After delay" },
]);

/** automations.goal_metric (automation-level Goal, Automation Settings > Goals tab) */
const AUTOMATION_GOAL_METRIC_CODES = Object.freeze([
  { value: "entered_automation", label: "Entered Automation" },
  { value: "exited_automation_early", label: "Exited Automation Early" },
  { value: "completed_automation", label: "Completed Automation" },
]);

/** Message-level (Send Email / Send push notification / Send WhatsApp message) node ke "Goal"
    ka Metric dropdown - automation-level AUTOMATION_GOAL_METRIC_CODES (Entered/Exited/Completed Automation)
    se alag h, kyunki yeh ek individual message ke engagement/conversion metrics hai.
    "Sessions Attribution" aur "Add custom conversion" abhi locked/premium hain (koi backend/
    paid-plan gate nahi h, sirf design ke according disabled rakha h) - jab unlock karna ho to
    sirf `locked: true` hata dena. */
const AUTOMATION_MESSAGE_GOAL_METRIC_GROUPS = Object.freeze([
  {
    group: "Engagement",
    options: [
      { value: "delivered", label: "Delivered" },
      { value: "unique_opens", label: "Unique Opens" },
      { value: "click_to_open_rate", label: "Click-to-Open Rate" },
      { value: "bounced", label: "Bounced" },
      { value: "spam", label: "Spam" },
      { value: "unsubscribed", label: "Unsubscribed" },
    ],
  },
  {
    group: "Conversions",
    options: [
      { value: "sessions_attribution", label: "Sessions Attribution", locked: true },
      { value: "clicks", label: "Clicks" },
    ],
  },
]);

/** automations.schedule_start_type */
const AUTOMATION_SCHEDULE_START_CODES = Object.freeze([
  { value: 1, label: "Immediately" },
  { value: 2, label: "Scheduled datetime" },
]);

/** automations.schedule_end_type */
const AUTOMATION_SCHEDULE_END_CODES = Object.freeze([
  { value: 1, label: "Never" },
  { value: 2, label: "End datetime" },
]);

/** automations.graph node types (key is used for the node type in the graph) */
const AUTOMATION_NODE_TYPE_CODES = Object.freeze([
  { value: 1, key: "entrance", label: "Entrance", badgeClass: "badge rounded-pill bg-light-primary", color: '#4c5fd5', group: "Flow" },
  { value: 2, key: "wait", label: "Wait", badgeClass: "badge rounded-pill bg-light-info", color: '#f59e0b', group: "Flow" },
  { value: 4, key: "condition", label: "Condition", badgeClass: "badge rounded-pill bg-light-info", color: '#ec4899', group: "Flow" },
  { value: 5, key: "exit", label: "Exit", badgeClass: "badge rounded-pill bg-light-primary", color: '#4c5fd5', group: "Flow" },
  { value: 3, key: "push", label: "Push Notification", badgeClass: "badge rounded-pill bg-light-primary", color: '#4c5fd5', group: "Messages" },
  // { value: 6, key: "email", label: "Email", badgeClass: "badge rounded-pill bg-light-primary", color: '#0891b2', group: "Messages" },     // phase 4
  // { value: 7, key: "in_app", label: "In-App Message", badgeClass: "badge rounded-pill bg-light-primary", color: '#7c3aed', group: "Messages" },    // phase 4
  // { value: 8, key: "sms", label: "SMS/RCS", badgeClass: "badge rounded-pill bg-light-primary", color: '#0d9488', group: "Messages" },       // phase 4
  { value: 9, key: "whatsapp", label: "WhatsApp", badgeClass: "badge rounded-pill bg-light-primary", color: '#25d366', group: "Messages" },  // phase 4
  { value: 10, key: "tag", label: "Tag", badgeClass: "badge rounded-pill bg-light-info", color: '#d97706', group: "Flow" },      // phase 3
  // Internal merge point of a Condition block (where its Yes/No branches join back) - never shown
  // in the insert menu, rendered as an invisible point; the engine just passes through it.
  { value: 11, key: "join", label: "Merge", badgeClass: "badge rounded-pill bg-light-secondary", color: '#9ca3af', group: "Internal" },
]);

/** automation_enrollments.status */
const AUTOMATION_ENROLL_STATUS_CODES = Object.freeze([
  { value: 1, label: "Queued", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 2, label: "Active", badgeClass: "badge rounded-pill bg-light-primary" },
  { value: 3, label: "Waiting", badgeClass: "badge rounded-pill bg-light-info" },
  { value: 4, label: "Completed", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 5, label: "Exited early", badgeClass: "badge rounded-pill bg-light-warning" },
  { value: 6, label: "Failed", badgeClass: "badge rounded-pill bg-light-danger" },
  { value: 7, label: "Suppressed", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

/** automation_enrollments.exit_reason */
const AUTOMATION_EXIT_REASON_CODES = Object.freeze([
  { value: 1, label: "Completed" },
  { value: 2, label: "User became active" },
  { value: 3, label: "Custom event" },
  { value: 4, label: "Left audience" },
  { value: 5, label: "Entered a segment" },
  { value: 6, label: "Automation ended" },
]);

/** wait-node time unit */
const AUTOMATION_WAIT_UNIT_CODES = Object.freeze([
  { value: 1, key: "minutes", label: "Minutes" },
  { value: 2, key: "hours", label: "Hours" },
  { value: 3, key: "days", label: "Days" },
  { value: 4, key: "weeks", label: "Weeks" },
  { value: 5, key: "months", label: "Months" },
  { value: 6, key: "years", label: "Years" },
]);

const getAutomationNodeType = (key, key_name = 'key') => {
    return AUTOMATION_NODE_TYPE_CODES.find((item) => item[key_name] === key) || null;
}

/**
 * Persistable automation canvas: React Flow runtime fields (measured, selected, dragging,
 * mergeTail, conditionSelfMergeY, drag-ghost) DB me nahi jaate. UI + engine dono yahi shape padhte hain.
 */
function serializeAutomationGraph(graph) {
  const src = typeof graph === "string" ? safeJsonParse(graph, null) : graph;
  if (!src || typeof src !== "object") {
    return { nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 } };
  }
  const nodes = (Array.isArray(src.nodes) ? src.nodes : [])
    .filter((n) => n && n.id && n.id !== "__drag-ghost__")
    .map((n) => {
      const raw = n.data && typeof n.data === "object" && !Array.isArray(n.data) ? n.data : {};
      const data = { ...raw };
      delete data.conditionSelfMergeY;
      delete data.mergeTail;
      return {
        id: String(n.id),
        type: Number(n.type),
        position: {
          x: Number(n.position?.x) || 0,
          y: Number(n.position?.y) || 0,
        },
        data,
      };
    });
  const edges = (Array.isArray(src.edges) ? src.edges : [])
    .filter((e) => e && e.id && e.source && e.target && e.source !== "__drag-ghost__" && e.target !== "__drag-ghost__")
    .map((e) => {
      const edge = {
        id: String(e.id),
        source: String(e.source),
        target: String(e.target),
      };
      if (e.sourceHandle) edge.sourceHandle = String(e.sourceHandle);
      if (e.targetHandle) edge.targetHandle = String(e.targetHandle);
      if (e.data && typeof e.data === "object" && e.data.branch) {
        edge.data = { branch: String(e.data.branch) };
      }
      return edge;
    });
  const vp = src.viewport && typeof src.viewport === "object" ? src.viewport : {};
  return {
    nodes,
    edges,
    viewport: {
      x: Number(vp.x) || 0,
      y: Number(vp.y) || 0,
      zoom: Number(vp.zoom) || 1,
    },
  };
}

/**
 * Client API key row `status` (admin list): 1 = active, 0 or any other = disabled.
 * Same pill badge pattern as subscriptions — `badgeClass` uses App.css `.badge.bg-light-*`.
 */
/** Single source: numeric `value` (matches DB `status`), labels, badge classes. */
const API_KEY_STATUS = Object.freeze([
  { value: 1, label: "Active", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 0, label: "Disabled", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

function getApiKeyDisplayStatus(code) {
  const list = Array.isArray(API_KEY_STATUS) ? API_KEY_STATUS : [];
  const n = code == null || code === "" ? NaN : Number(code);
  const entry = list.find((x) => x.value === n);
  if (entry) return { label: entry.label, badgeClass: entry.badgeClass };
  return { label: code != null && code !== "" ? String(code) : "—", badgeClass: null };
}

/** Partner marketplace catalog `partner_type` (INT). */
const PARTNER_TYPE = Object.freeze([
  { value: 1, label: "Ad network" },
  { value: 2, label: "Agency" },
  { value: 3, label: "Tech partner" },
]);

/** Admin catalog partner_fields / credentials field.type (string). */
const PARTNER_CREDENTIAL_FIELD_TYPE = Object.freeze([
  { value: "text", label: "Text" },
  { value: "secret", label: "Secret" },
]);

/** Partner network endpoint HTTP methods. */
const PARTNER_HTTP_METHOD = Object.freeze([
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
]);

/** Known per-app integration setting keys (collaborate_partner_settings.setting_key). */
const PARTNER_SETTING_KEY = Object.freeze({
  install_postback_enabled: "install_postback_enabled",
  install_users_from: "install_users_from",
  in_app_postbacks_enabled: "in_app_postbacks_enabled",
  in_app_window_unit: "in_app_window_unit",
  in_app_window_value: "in_app_window_value",
});

/** Postback audience filter: who to send for. */
const PARTNER_USERS_FROM = Object.freeze([
  { value: 1, label: "This partner only" },
  { value: 2, label: "All media sources" },
]);

/** In-app postback attribution window unit. */
const PARTNER_IN_APP_WINDOW_UNIT = Object.freeze([
  { value: 1, label: "Hours" },
  { value: 2, label: "Days" },
  { value: 3, label: "Months" },
  { value: 4, label: "Lifetime" },
]);

/** What values to include on in-app postbacks. */
const PARTNER_INCLUDE_VALUES = Object.freeze([
  { value: 0, label: "No values & no revenue" },
  { value: 1, label: "Values only" },
  { value: 2, label: "Values & revenue" },
]);

function getPartnerTypeLabel(code) {
  const entry = PARTNER_TYPE.find((x) => x.value === Number(code));
  return entry ? entry.label : "—";
}

function getPartnerUsersFromLabel(code) {
  const entry = PARTNER_USERS_FROM.find((x) => x.value === Number(code));
  return entry ? entry.label : "—";
}

/** Required credential keys missing from config_values (labels for errors). */
function missingRequiredPartnerConfigFields(credentials, values) {
  const missing = [];
  const vals = values && typeof values === "object" && !Array.isArray(values) ? values : {};
  const secretType = PARTNER_CREDENTIAL_FIELD_TYPE.find((t) => t.value === "secret")?.value;
  (Array.isArray(credentials) ? credentials : []).forEach((field) => {
    if (!field?.key || Number(field.required) !== 1) return;
    if (String(field.type ?? "") === secretType) return;
    const v = vals[field.key];
    if (v == null || String(v).trim() === "") {
      missing.push(String(field.label || field.key));
    }
  });
  return missing;
}

/**
 * Sent messages list: delivery state from send-job lifecycle (or sent_count).
 * DB/API `value` is integer (1|2|3|4). UI shows `label`.
 */
const SENT_MESSAGE_DELIVERY_STATUS = Object.freeze([
  { value: 1, label: "Delivered", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, label: "Failed", badgeClass: "badge rounded-pill bg-light-danger" },
  { value: 3, label: "Pending", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 4, label: "Processing", badgeClass: "badge rounded-pill bg-light-info text-dark" },
]);

/** notification_send_jobs.status — keep in sync with model comment + SQL. */
const NOTIFICATION_SEND_JOB_STATUS = Object.freeze({
  queued: 1,
  running: 2,
  done: 3,
  failed: 4,
});

/**
 * whatsapp_messages.status — DB TINYINT. Meta webhook maps to these values.
 * Keep in sync with model + query-migration.sql.
 * Index: [0]=queued [1]=sent [2]=delivered [3]=read [4]=failed
 */
const WHATSAPP_MESSAGE_STATUS = Object.freeze([
  { value: 1, label: "Queued", key: "queued", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 2, label: "Sent", key: "sent", badgeClass: "badge rounded-pill bg-light-info text-dark" },
  { value: 3, label: "Delivered", key: "delivered", badgeClass: "badge rounded-pill bg-light-primary" },
  { value: 4, label: "Read", key: "read", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 5, label: "Failed", key: "failed", badgeClass: "badge rounded-pill bg-light-danger" },
]);

/**
 * whatsapp_campaigns.status — DB TINYINT. Lifecycle of a Send Message / campaign hit.
 * Keep in sync with model + query-migration.sql.
 * Index: [0]=draft [1]=scheduled [2]=running [3]=completed [4]=cancelled [5]=failed
 */
const WHATSAPP_CAMPAIGN_STATUS = Object.freeze([
  { value: 1, label: "Draft", key: "draft", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 2, label: "Scheduled", key: "scheduled", badgeClass: "badge rounded-pill bg-light-info text-dark" },
  { value: 3, label: "Running", key: "running", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 4, label: "Completed", key: "completed", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 5, label: "Cancelled", key: "cancelled", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 6, label: "Failed", key: "failed", badgeClass: "badge rounded-pill bg-light-danger" },
]);

/**
 * whatsapp_templates.status — DB TINYINT. Meta's API returns these as strings;
 * getWhatsAppTemplateStatusCode()/getWhatsAppTemplateStatusKey() convert at the DB
 * read/write boundary so this stays the only place string<->int is defined.
 */
const WHATSAPP_TEMPLATE_STATUS_CODES = Object.freeze([
  { value: 0, key: "UNKNOWN", label: "Unknown", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 1, key: "APPROVED", label: "Approved", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, key: "PENDING", label: "Pending review", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 3, key: "IN_APPEAL", label: "In appeal", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 4, key: "REJECTED", label: "Rejected", badgeClass: "badge rounded-pill bg-light-danger" },
  { value: 5, key: "PAUSED", label: "Paused", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 6, key: "DISABLED", label: "Disabled", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 7, key: "FLAGGED", label: "Flagged", badgeClass: "badge rounded-pill bg-light-danger" },
]);

function getWhatsAppTemplateStatusCode(status) {
  const key = String(status || "").trim().toUpperCase();
  const entry = WHATSAPP_TEMPLATE_STATUS_CODES.find((x) => x.key === key);
  return entry ? entry.value : 0;
}

function getWhatsAppTemplateStatusKey(code) {
  const entry = findOptionByValue(WHATSAPP_TEMPLATE_STATUS_CODES, code);
  return entry ? entry.key : "UNKNOWN";
}

/**
 * whatsapp_templates.quality_score — DB TINYINT. Meta's template quality_score is
 * `{score: "GREEN"|"YELLOW"|"RED"|"UNKNOWN"}` (or occasionally a bare string) - same scale as
 * phone-number quality_rating (getWhatsAppQualityDisplay), so reuse its GREEN/YELLOW/RED/UNKNOWN keys.
 */
const WHATSAPP_TEMPLATE_QUALITY_CODES = Object.freeze([
  { value: 0, key: "UNKNOWN", label: "Pending", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 1, key: "GREEN", label: "High", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, key: "YELLOW", label: "Medium", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 3, key: "RED", label: "Low", badgeClass: "badge rounded-pill bg-light-danger" },
]);

function getWhatsAppTemplateQualityCode(score) {
  const key = String(score || "").trim().toUpperCase();
  const entry = WHATSAPP_TEMPLATE_QUALITY_CODES.find((x) => x.key === key);
  return entry ? entry.value : 0;
}

function getWhatsAppTemplateQualityKey(code) {
  const entry = findOptionByValue(WHATSAPP_TEMPLATE_QUALITY_CODES, code);
  return entry ? entry.key : "UNKNOWN";
}

/** whatsapp_templates.category — DB TINYINT (int-coded, same convention as status above). */
const WHATSAPP_TEMPLATE_CATEGORY_CODES = Object.freeze([
  { value: 0, key: "UNKNOWN", label: "Unknown", badgeClass: "badge rounded-pill bg-light-secondary" },
  { value: 1, key: "MARKETING", label: "Marketing", badgeClass: "badge rounded-pill bg-light-primary" },
  { value: 2, key: "UTILITY", label: "Utility", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 3, key: "AUTHENTICATION", label: "Authentication", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
]);

function getWhatsAppTemplateCategoryCode(category) {
  const key = String(category || "").trim().toUpperCase();
  const entry = WHATSAPP_TEMPLATE_CATEGORY_CODES.find((x) => x.key === key);
  return entry ? entry.value : 0;
}

function getWhatsAppTemplateCategoryKey(code) {
  const entry = findOptionByValue(WHATSAPP_TEMPLATE_CATEGORY_CODES, code);
  return entry ? entry.key : "UNKNOWN";
}

/**
 * whatsapp_templates.rejected_reason — DB TINYINT (int-coded). Meta's documented values for
 * message_template_status_update's `reason` / the template object's `rejected_reason`; NONE
 * covers a non-rejected template and OTHER/UNKNOWN cover anything Meta adds later.
 */
const WHATSAPP_TEMPLATE_REJECTED_REASON_CODES = Object.freeze([
  { value: 0, key: "UNKNOWN", label: "Unknown" },
  { value: 1, key: "NONE", label: "None" },
  { value: 2, key: "ABUSIVE_CONTENT", label: "Abusive content" },
  { value: 3, key: "INCORRECT_CATEGORY", label: "Incorrect category" },
  { value: 4, key: "INVALID_FORMAT", label: "Invalid format" },
  { value: 5, key: "SCAM", label: "Scam" },
  { value: 6, key: "TAG_CONTENT_MISMATCH", label: "Tag/content mismatch" },
  { value: 7, key: "OTHER", label: "Other" },
]);

function getWhatsAppTemplateRejectedReasonCode(reason) {
  const key = String(reason || "").trim().toUpperCase();
  if (!key) return 0;
  const entry = WHATSAPP_TEMPLATE_REJECTED_REASON_CODES.find((x) => x.key === key);
  return entry ? entry.value : 7; // OTHER - Meta added a value we don't know about yet
}

function getWhatsAppTemplateRejectedReasonKey(code) {
  const entry = findOptionByValue(WHATSAPP_TEMPLATE_REJECTED_REASON_CODES, code);
  return entry ? entry.key : "UNKNOWN";
}

/**
 * Scheduled notifications: DB TINYINT (`scheduled_notifications.status`).
 * Own list (not shared with sent) so values/labels can change independently.
 */
const SCHEDULED_MESSAGE_STATUS = Object.freeze([
  { value: 1, label: "Delivered", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, label: "Failed", badgeClass: "badge rounded-pill bg-light-danger" },
  { value: 3, label: "Pending", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 4, label: "Processing", badgeClass: "badge rounded-pill bg-light-info text-dark" },
  { value: 5, label: "Cancelled", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

/** Sent + scheduled source (`sent_from` / `sentFrom`). UI label: Source. DB/API integer. Not user `created_by`. */
const MESSAGE_CREATED_BY_OPTIONS = Object.freeze([
  { value: 1, label: "API", badgeClass: "badge rounded-pill bg-light-info" },
  { value: 2, label: "Dashboard", badgeClass: "badge rounded-pill bg-light-primary" },
]);

/** Bulk-send audience. DB/API integer. 1=all subscriptions, 2=segment, 3=test subscriptions. */
const MESSAGE_AUDIENCE_OPTIONS = Object.freeze([
  { value: 1, label: "All subscriptions", badgeClass: "badge rounded-pill bg-light-primary" },
  { value: 2, label: "Segment", badgeClass: "badge rounded-pill bg-light-info text-dark" },
  { value: 3, label: "Test subscriptions", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
]);

/** Immediate vs scheduled send (UI label: Type). DB/API integer. */
const MESSAGE_SEND_KIND_OPTIONS = Object.freeze([
  { value: 1, label: "Immediate", badgeClass: "badge rounded-pill bg-light-primary" },
  { value: 2, label: "Scheduled", badgeClass: "badge rounded-pill bg-light-info text-dark" },
]);

/** Event filter sentinel: rows with revenue > 0 (dashboard booking). Not a stored event_type. */
const EVENT_METRIC_OPTION = Object.freeze({
  value: "__revenue__",
  label: "Revenue",
});

/** Marks an event-metric entry (`metric:<label>`) in a list that mixes raw events and metrics (Funnel steps). */
const EVENT_METRIC_PREFIX = "metric:";

/** A funnel step can hold several events (Signup or Login); they are stored in one string joined by this. */
const FUNNEL_STEP_SEPARATOR = "|";
/** Colors for the events inside one multi-event funnel step (graph segments + legend dots), in step order. */
const FUNNEL_EVENT_COLORS = Object.freeze(["#4346ce", "#8f93ea", "#f59e0b", "#0e9f6e", "#db2777", "#0284c7"]);
/** "Nice" chart axis steps: multiplied by 10^n of the data size (1, 2, 2.5, 5, 10 → 10, 20, 25, 50, 100 → 1k, 2k, 2.5k …). */
const CHART_AXIS_STEP_MULTIPLIERS = Object.freeze([1, 2, 2.5, 5, 10]);

/** Engage → Funnel limits and option lists shared by the page and the API (so both always agree). */
const FUNNEL_MIN_STEPS = 2;
const FUNNEL_MAX_STEPS = 8;
const FUNNEL_USERS_PAGE_SIZE = 25;
const FUNNEL_USERS_PAGE_MAX = 100;
/** "Message these users": most users one funnel segment keeps (newest first). */
const FUNNEL_SEGMENT_USER_CAP = 3000;
/** Segment rule that holds a fixed list of app users picked from a funnel step. */
const FUNNEL_SEGMENT_RULE_ID = "funnel-users";
/** Biggest groups a breakdown shows; the rest are merged into "Other". */
const FUNNEL_BREAKDOWN_MAX_GROUPS = 10;
const FUNNEL_BREAKDOWN_OPTIONS = Object.freeze([
  { value: "", label: "No breakdown" },
  { value: "platform", label: "Platform" },
  { value: "source", label: "Source (Campaign / Organic)" },
  { value: "campaign", label: "Campaign" },
  { value: "country", label: "Country" },
  { value: "state", label: "State" },
  { value: "city", label: "City" },
]);
const FUNNEL_SOURCE_OPTIONS = Object.freeze([
  { value: "", label: "All sources" },
  { value: "0", label: "Campaign" },
  { value: "1", label: "Organic" },
]);
const FUNNEL_WINDOW_OPTIONS = Object.freeze([
  { value: "", label: "No time limit" },
  { value: "1", label: "Within 1 day" },
  { value: "7", label: "Within 7 days" },
  { value: "14", label: "Within 14 days" },
  { value: "30", label: "Within 30 days" },
  { value: "90", label: "Within 90 days" },
]);
/** Filters a saved funnel remembers. */
/** funnel_reports.mode — the same list is used to save (key -> value) and to read back (value -> key). */
const FUNNEL_MODE_CODES = Object.freeze([
  { value: 1, key: "ordered", label: "In order" },
  { value: 3, key: "strict", label: "Strict" },
  { value: 2, key: "any", label: "Any order" },
]);

const FUNNEL_FILTER_KEYS = Object.freeze(["date_preset", "date_from", "date_to", "campaign_id", "is_organic", "country", "platform", "state", "city", "breakdown", "exclude_events"]);

/**
 * Support custom fields type options.
 * `value` is saved in DB as `field_type`.
 */
const SUPPORT_FIELD_TYPE_OPTIONS = Object.freeze([
  { value: "dropdown", label: "Dropdown" },
  { value: "text", label: "Text" },
  { value: "checkbox", label: "Checkbox" },
  { value: "number", label: "Number" },
  // { value: "multiline", label: "Multi-line" },
  { value: "date", label: "Date" },
  // { value: "multi_select", label: "Multi-select" },
  // { value: "decimal", label: "Decimal" },
  // { value: "regex", label: "Regex" },
  // { value: "lookup_relationship", label: "Lookup relationship" },
]);

/**
 * Support ticket attachments — edit only here (extension → MIME list).
 * Used by panel upload, client API, multer filter, and `<input accept>`.
 */
const SUPPORT_TICKET_FILE_TYPES = Object.freeze({
  jpeg: Object.freeze(["image/jpeg"]),
  jpg: Object.freeze(["image/jpeg"]),
  png: Object.freeze(["image/png"]),
  gif: Object.freeze(["image/gif"]),
  webp: Object.freeze(["image/webp"]),
  pdf: Object.freeze(["application/pdf"]),
});

/** Derived extension list — do not edit; change {@link SUPPORT_TICKET_FILE_TYPES} only. */
const SUPPORT_TICKET_ALLOWED_FILE_TYPES = Object.freeze(Object.keys(SUPPORT_TICKET_FILE_TYPES));

/** Default relative upload root for app support ticket files in stored message bodies. */
const APP_SUPPORT_UPLOADS_PATH = "/uploads/app_support";

/** Map `/uploads/...` URL prefix to a filesystem path segment, e.g. `uploads/app_support`. */
function uploadsPathToFilesystemRelative(uploadPath = APP_SUPPORT_UPLOADS_PATH) {
  return String(uploadPath || APP_SUPPORT_UPLOADS_PATH).replace(/^\/+/, "");
}

/** Build `/uploads/app_support/<fileName>` for a stored upload reference. */
function buildUploadRelativeUrl(fileName, uploadPath = APP_SUPPORT_UPLOADS_PATH) {
  const base = String(uploadPath || APP_SUPPORT_UPLOADS_PATH).replace(/\/+$/, "");
  const name = String(fileName || "").replace(/^\/+/, "");
  return name ? `${base}/${name}` : base;
}

/** Strip trailing metadata e.g. `floor.png (image/png, 508978 bytes)` → `floor.png`. */
function normalizeFileName(fileName) {
  const raw = String(fileName || "").trim();
  if (!raw) return "";
  const withoutMeta = raw.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return withoutMeta || raw;
}

function getFileExtension(filenameOrUrl) {
  const fromPath = String(filenameOrUrl || "").split("?")[0].split("#")[0];
  const last = normalizeFileName(fromPath.split("/").pop() || fromPath);
  const idx = last.lastIndexOf(".");
  if (idx === -1) return "";
  return last.slice(idx + 1).toLowerCase();
}

function isImageExt(ext, fileTypes = SUPPORT_TICKET_FILE_TYPES) {
  const normalized = String(ext || "").toLowerCase().replace(/^\./, "");
  if (!normalized || !fileTypes[normalized]) return false;
  return fileTypes[normalized].some((mime) => String(mime).startsWith("image/"));
}

function isPdfExt(ext, fileTypes = SUPPORT_TICKET_FILE_TYPES) {
  const normalized = String(ext || "").toLowerCase().replace(/^\./, "");
  return normalized === "pdf" && Boolean(fileTypes.pdf);
}

function inferMimeFromLabel(fileName) {
  const match = String(fileName || "").match(/\(([^,)]+)/);
  const token = String(match?.[1] || "").trim().toLowerCase();
  return token.includes("/") ? token : "";
}

/** Resolve preview kind (image/pdf/other) from filename, URL, or embedded mime label. */
function resolveFileKind(fileName, url, fileTypes = SUPPORT_TICKET_FILE_TYPES) {
  const extFromName = getFileExtension(fileName);
  const extFromUrl = getFileExtension(url);
  const ext = extFromName || extFromUrl;
  if (isImageExt(ext, fileTypes)) return { ext, isImg: true, isPdf: false };
  if (isPdfExt(ext, fileTypes)) return { ext, isImg: false, isPdf: true };
  const mime = inferMimeFromLabel(fileName);
  if (mime.startsWith("image/")) {
    const mimeExt = mime.split("/")[1] || "";
    return { ext: mimeExt || ext, isImg: true, isPdf: false };
  }
  if (mime === "application/pdf") return { ext: "pdf", isImg: false, isPdf: true };
  return { ext, isImg: false, isPdf: false };
}

/** Build absolute URL from a relative `/uploads/...` path stored in message text. */
function buildUploadAbsoluteUrl(maybeRelativeUrl, baseUrl = "") {
  const raw = String(maybeRelativeUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(baseUrl || "").replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function escapeRegexLiteral(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeSupportPlainHtmlText(raw) {
  return String(raw || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Plain support message → safe HTML: blank lines separate <p>; single newlines become <br/> inside <p>. */
function plainSupportTextToHtml(text, escapeFn = escapeSupportPlainHtmlText) {
  const escape = typeof escapeFn === "function" ? escapeFn : escapeSupportPlainHtmlText;
  const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!normalized) return "";
  return normalized
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escape(block).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

/**
 * Parse attachments appended to stored message bodies.
 * Format: `Attachments:\n- <name> -> /uploads/app_support/<file>`
 *
 * @param {string} rawText
 * @param {string} [baseUrl]
 * @param {{ uploadPath?: string }} [options] — default `/uploads/app_support`; override for other modules
 */
function extractFilesFromText(rawText, baseUrl = "", options = {}) {
  const uploadPath = String(options.uploadPath || APP_SUPPORT_UPLOADS_PATH).replace(/\/+$/, "");
  const uploadPathRe = escapeRegexLiteral(uploadPath);

  const raw = String(rawText || "");
  if (!raw.trim()) return { cleanText: "-", attachments: [] };

  const lines = raw.split("\n");
  const attachments = [];
  let inAttachmentsBlock = false;
  const keptLines = [];
  const bulletLineRe = new RegExp(`^-\\s*(.+?)\\s*->\\s*(${uploadPathRe}/\\S+)`, "i");

  for (const line of lines) {
    const rawLine = String(line || "");
    const trimmed = rawLine.trim();

    if (/^attachments\s*:/i.test(trimmed)) {
      inAttachmentsBlock = true;
      continue;
    }

    if (inAttachmentsBlock) {
      const match = trimmed.match(bulletLineRe);
      if (match) {
        const fileName = normalizeFileName(match[1].trim());
        const relativeUrl = match[2].trim();
        attachments.push({
          fileName,
          url: buildUploadAbsoluteUrl(relativeUrl, baseUrl),
          relativeUrl,
        });
        continue;
      }

      if (!/^-/.test(trimmed)) {
        inAttachmentsBlock = false;
      } else {
        continue;
      }
    }

    keptLines.push(rawLine);
  }

  const mdRegex = new RegExp(`\\[([^\\]]+)\\]\\((${uploadPathRe}/[^)\\s]+)\\)`, "gi");
  let mdMatch;
  while ((mdMatch = mdRegex.exec(raw)) !== null) {
    attachments.push({
      fileName: normalizeFileName(String(mdMatch[1] || "").trim()) || "Attachment",
      url: buildUploadAbsoluteUrl(mdMatch[2], baseUrl),
      relativeUrl: mdMatch[2],
    });
  }

  const urlRegex = new RegExp(`(${uploadPathRe}/[A-Za-z0-9._-]+\\.[A-Za-z0-9]+)(\\?[^\\s]*)?`, "g");
  let uMatch;
  while ((uMatch = urlRegex.exec(raw)) !== null) {
    const rel = String(uMatch[1] || "").trim();
    if (!rel) continue;
    const fileName = normalizeFileName(rel.split("/").pop() || "Attachment");
    attachments.push({
      fileName,
      url: buildUploadAbsoluteUrl(rel, baseUrl),
      relativeUrl: rel,
    });
  }

  const seen = new Set();
  const deduped = [];
  for (const att of attachments) {
    const key = String(att.relativeUrl || att.url || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(att);
  }

  return { cleanText: keptLines.join("\n"), attachments: deduped };
}

/** `<input accept="...">` from {@link SUPPORT_TICKET_FILE_TYPES}. */
function buildSupportTicketFileAcceptAttribute(fileTypes = SUPPORT_TICKET_FILE_TYPES) {
  const parts = new Set();
  for (const [ext, mimes] of Object.entries(fileTypes)) {
    const clean = String(ext).trim().toLowerCase().replace(/^\./, "");
    if (!clean) continue;
    parts.add(`.${clean}`);
    for (const mime of mimes || []) {
      parts.add(mime);
    }
  }
  return [...parts].join(",");
}

function isSupportTicketFileAllowed(file, fileTypes = SUPPORT_TICKET_FILE_TYPES) {
  if (!file) return false;
  const allowedExts = Object.keys(fileTypes);
  const name = String(file.name || "");
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (ext && allowedExts.includes(ext)) return true;
  const mime = String(file.type || "").toLowerCase();
  for (const mimes of Object.values(fileTypes)) {
    if (mimes && mimes.includes(mime)) return true;
  }
  return false;
}

/** Default Active/Inactive status badges (lists with only these two states). */
const DEFAULT_STATUS = Object.freeze([
  { value: 1, label: "Active", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 0, label: "Inactive", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

function getStatusDisplay(code) {
  const n = Number(code) === 1 ? 1 : 0;
  const entry = DEFAULT_STATUS.find((x) => x.value === n);
  return entry || DEFAULT_STATUS[0];
}

/** Staff status badges (Staff page + Customer Support → Team Members). */
const APP_STAFF_STATUS = Object.freeze([
  { value: 1, label: "Active", badgeClass: "badge rounded-pill bg-light-success" },
  {
    value: 2,
    label: "Pending activation",
    badgeClass: "badge rounded-pill bg-light-warning text-dark",
  },
  { value: 0, label: "Inactive", badgeClass: "badge rounded-pill bg-light-secondary" },
]);

/** Resolve staff status badge from `{ status, is_member_active }`. */
function getStaffStatusDisplay(member) {
  const statusOn = Number(member?.status) === 1;
  if (!statusOn) {
    return APP_STAFF_STATUS.find((x) => x.value === 0) || APP_STAFF_STATUS[2];
  }
  if (!member?.is_member_active) {
    return APP_STAFF_STATUS.find((x) => x.value === 2) || APP_STAFF_STATUS[1];
  }
  return APP_STAFF_STATUS.find((x) => x.value === 1) || APP_STAFF_STATUS[0];
}

/** App support conversation status values (DB/API): 0=new (untouched — no team-member reply yet), 1=open, 2=pending, 3=closed. */
const APP_SUPPORT_CONVERSATION_STATUS = Object.freeze([
  { value: 1, label: "Open", badgeClass: "badge rounded-pill bg-light-success" },
  { value: 2, label: "Pending", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
  { value: 3, label: "Closed", badgeClass: "badge rounded-pill bg-light-secondary" },
  // Appended (not first) so the normalize() fallback below stays "Open" for unexpected values.
  { value: 0, label: "New", badgeClass: "badge rounded-pill bg-light-primary" },
]);

/** App support message sender values (DB/API): 1=client, 2=admin, 3=email. */
const APP_SUPPORT_MESSAGE_SENDER_TYPE = Object.freeze([
  { value: 1, label: "App User" },
  { value: 2, label: "Team Member" },
  { value: 3, label: "Email Reply" },
]);

/** App support message channel values (DB/API): 1=api, 2=panel, 3=email. */
const APP_SUPPORT_MESSAGE_SOURCE_CHANNEL = Object.freeze([
  { value: 1, label: "API" },
  { value: 2, label: "Panel" },
  { value: 3, label: "Email" },
]);

/**
 * Conversation origin for `app_support_conversations.source` (DB tinyint).
 * Edit only `value`, `label`, `badgeClass` here — everything else reads from this array automatically.
 */
const APP_SUPPORT_CONVERSATION_SOURCE = Object.freeze([
  { value: 1, label: "App", badgeClass: "badge bg-info text-dark" },
  { value: 2, label: "Email", badgeClass: "badge bg-secondary" },
  { value: 3, label: "Admin panel", badgeClass: "badge bg-primary" },
]);

/** Provenance for app_support_* rows (DB tinyint): 1=NotifySphere (default), 2=Zendesk import. */
const APP_SUPPORT_RECORD_ORIGIN = Object.freeze([
  { value: 1, label: "NotifySphere", badgeClass: "badge bg-primary" },
  { value: 2, label: "Zendesk", badgeClass: "badge bg-dark" },
]);

/** Coerce DB/API integer to a valid `value` from APP_SUPPORT_CONVERSATION_SOURCE; falls back to first entry. */
function normalizeAppSupportConversationSource(raw) {
  const fallback = Number(APP_SUPPORT_CONVERSATION_SOURCE[0]?.value ?? 1);
  const n = Number(raw);
  const allowed = new Set(APP_SUPPORT_CONVERSATION_SOURCE.map((x) => Number(x.value)));
  return allowed.has(n) ? n : fallback;
}

function getAppSupportConversationSourceDisplay(raw) {
  const value = normalizeAppSupportConversationSource(raw);
  const entry = APP_SUPPORT_CONVERSATION_SOURCE.find((x) => Number(x.value) === value);
  return entry || APP_SUPPORT_CONVERSATION_SOURCE[0];
}

function normalizeAppSupportRecordOrigin(raw) {
  const fallback = Number(APP_SUPPORT_RECORD_ORIGIN[0]?.value ?? 1);
  const n = Number(raw);
  const allowed = new Set(APP_SUPPORT_RECORD_ORIGIN.map((x) => Number(x.value)));
  return allowed.has(n) ? n : fallback;
}

function getAppSupportRecordOriginDisplay(raw) {
  const value = normalizeAppSupportRecordOrigin(raw);
  const entry = APP_SUPPORT_RECORD_ORIGIN.find((x) => Number(x.value) === value);
  return entry || APP_SUPPORT_RECORD_ORIGIN[0];
}

function normalizeAppSupportConversationStatus(raw) {
  const n = Number(raw);
  const allowed = APP_SUPPORT_CONVERSATION_STATUS.map((x) => Number(x.value));
  return allowed.includes(n) ? n : Number(APP_SUPPORT_CONVERSATION_STATUS[0]?.value || 1);
}

function isAppSupportConversationClosed(raw) {
  return normalizeAppSupportConversationStatus(raw) === 3;
}

function getAppSupportConversationStatusDisplay(raw) {
  const value = normalizeAppSupportConversationStatus(raw);
  const entry = APP_SUPPORT_CONVERSATION_STATUS.find((x) => x.value === value);
  return entry || APP_SUPPORT_CONVERSATION_STATUS[0];
}

function normalizeAppSupportMessageSenderType(raw) {
  const n = Number(raw);
  const allowed = APP_SUPPORT_MESSAGE_SENDER_TYPE.map((x) => Number(x.value));
  return allowed.includes(n) ? n : Number(APP_SUPPORT_MESSAGE_SENDER_TYPE[0]?.value || 1);
}

function getAppSupportMessageSenderDisplay(raw) {
  const value = normalizeAppSupportMessageSenderType(raw);
  const entry = APP_SUPPORT_MESSAGE_SENDER_TYPE.find((x) => x.value === value);
  return entry || APP_SUPPORT_MESSAGE_SENDER_TYPE[0];
}

function normalizeAppSupportMessageSourceChannel(raw) {
  const n = Number(raw);
  const allowed = APP_SUPPORT_MESSAGE_SOURCE_CHANNEL.map((x) => Number(x.value));
  return allowed.includes(n) ? n : Number(APP_SUPPORT_MESSAGE_SOURCE_CHANNEL[0]?.value || 1);
}

/**
 * UUID-like random id: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 * (Same implementation as legacy backend helper — single source.)
 */
function generateRandomString() {
  const randomHex = (length) =>
    Math.random().toString(16).substr(2, length).padEnd(length, "0");

  const part1 = randomHex(8);
  const part2 = randomHex(4);
  const part3 = randomHex(4);
  const part4 = randomHex(4);
  const part5 = randomHex(12);

  return `${part1}-${part2}-${part3}-${part4}-${part5}`;
}

/**
 * `users` table fields that must never go to the browser (API responses + localStorage).
 * Single list: backend `customUtils.sanitizeUserForClient` and `userDataToStorageString` both use this.
 */
const USER_CLIENT_SENSITIVE_KEYS = Object.freeze([
  "password",
  "twofa_secret",
  "twofa_recovery_codes",
  "accessToken",
  "refreshToken",
  "reset_token",
  "reset_token_expires_at",
]);

/**
 * Strip sensitive fields from dashboard user JSON before `localStorage` (defense in depth).
 * @param {string|object} userData JSON string from API or plain object
 * @returns {string} safe JSON string
 */
function userDataToStorageString(userData) {
  let parsed;
  try {
    parsed = typeof userData === "string" ? JSON.parse(userData) : { ...userData };
  } catch {
    return typeof userData === "string" ? userData : JSON.stringify(userData);
  }
  const clean = { ...parsed };
  for (const key of USER_CLIENT_SENSITIVE_KEYS) {
    delete clean[key];
  }
  return JSON.stringify(clean);
}

function getPlanDisplayName(planType) {
  return findOptionByValue(PLAN_TYPE_CODES, planType)?.label ?? "Free";
}

/** ─── Campaign / OneLink constants (shared frontend + backend) ─── */

const ONELINK_MODAL_STEP = Object.freeze([
  { value: 1, key: "general", label: "General settings" },
  { value: 2, key: "deeplink", label: "Deep linking" },
]);

const ONELINK_TEMPLATE = Object.freeze([
  { value: 1, label: "Android", templateId: "Ldto" },
  { value: 2, label: "iOS", templateId: "ios" },
  { value: 3, label: "Web", templateId: "web" },
]);

const ONELINK_CAMPAIGN_TYPE = Object.freeze([
  { value: 1, label: "Social-to-app" },
]);

function getCampaignTypeLabel(value) {
  const v = Number(value);
  const found = ONELINK_CAMPAIGN_TYPE.find((o) => o.value === v);
  return found ? found.label : "";
}

const ONELINK_MEDIA_SOURCE = Object.freeze([
  { value: 1, label: "Google_ads" },
  { value: 2, label: "Facebook_ads" },
  { value: 3, label: "Instagram_ads" },
  { value: 4, label: "Snapchat_ads" },
  { value: 5, label: "Twitter_ads" },
  { value: 6, label: "LinkedIn_ads" },
  { value: 7, label: "YouTube_ads" },
  { value: 8, label: "Pinterest_ads" },
  { value: 9, label: "TikTok_ads" },
  { value: 10, label: "Apple_search_ads" },
  { value: 11, label: "Microsoft_ads" },
  { value: 12, label: "Taboola" },
  { value: 13, label: "Outbrain" },
  { value: 14, label: "Unity_ads" },
  { value: 15, label: "IronSource" },
  { value: 16, label: "AppLovin" },
  { value: 17, label: "Email" },
  { value: 18, label: "SMS" },
  { value: 19, label: "Blog" },
  { value: 20, label: "Organic" },
  { value: 21, label: "Referral" },
  { value: 22, label: "Other" },
]);

const ONELINK_LINK_STATUS = Object.freeze([
  { value: 1, label: "Active", badgeClass: "engage-status-pill status-active" },
  { value: 2, label: "Paused", badgeClass: "engage-status-pill status-paused" },
  { value: 3, label: "Archived", badgeClass: "engage-status-pill status-archived" },
]);

const ONELINK_LINK_STATUS_ACTIVE = 1;

const ONELINK_SOCIAL_PREVIEW = Object.freeze([
  { value: 1, label: "Default (app store listing)", hint: "Uses your app name, icon, and store listing when the link is shared on social networks." },
  { value: 2, label: "Custom preview", hint: "Set your own title, description, and image for link previews on social posts." },
]);

const ONELINK_REDIRECT_TYPE = Object.freeze([
  { value: 1, androidLabel: "Google Play", iosLabel: "App Store", label: "Store listing" },
  { value: 2, androidLabel: "Custom URL", iosLabel: "Custom URL", label: "Custom URL" },
]);

const ONELINK_DEFAULTS = Object.freeze({
  experienceId: "social-to-app",
  mediaSourceValue: 1,
  templateValue: 1,
  socialPreviewValue: 1,
  redirectTypeValue: 1,
  linkStatusValue: 1,
});

function onelinkNormalizeOptionValue(list, value, defaultValue) {
  const hit = findOptionByValue(list, value);
  return hit ? hit.value : defaultValue;
}

function getMediaSourceLabel(value) {
  return findOptionByValue(ONELINK_MEDIA_SOURCE, value)?.label ?? ONELINK_MEDIA_SOURCE[0].label;
}

function getOnelinkTemplateEntry(value) {
  return findOptionByValue(ONELINK_TEMPLATE, value) ?? ONELINK_TEMPLATE[0];
}

function getOnelinkSocialPreviewEntry(value) {
  return findOptionByValue(ONELINK_SOCIAL_PREVIEW, value) ?? ONELINK_SOCIAL_PREVIEW[0];
}

function getOnelinkRedirectLabel(value, platform) {
  const entry = findOptionByValue(ONELINK_REDIRECT_TYPE, value) ?? ONELINK_REDIRECT_TYPE[0];
  if (platform === "android") return entry.androidLabel;
  if (platform === "ios") return entry.iosLabel;
  return entry.label;
}

function getLinkStatusDisplay(value) {
  const entry = findOptionByValue(ONELINK_LINK_STATUS, value);
  if (!entry) return { label: "—", badgeClass: "engage-status-pill" };
  return { label: entry.label, badgeClass: entry.badgeClass };
}

function buildDefaultLinkName(mediaSourceValue = ONELINK_DEFAULTS.mediaSourceValue) {
  const label = getMediaSourceLabel(mediaSourceValue);
  const d = new Date();
  const dateStr = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "_");
  return `Social-to-app_${label}_${dateStr}`;
}

function parseCustomParams(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

const DEFAULT_STATUS_OPTIONS = Object.freeze([
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
]);

/** Title-case person names: "jack Smith" → "Jack Smith". Leaves emails unchanged. */
function formatPersonDisplayName(input) {
  const s = String(input || "").trim();
  if (!s) return "";
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return s;
  const titleWord = (word) =>
    word
      .split(/(-)/)
      .map((part) => {
        if (part === "-" || !part) return part;
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join("");
  return s.split(/\s+/).filter(Boolean).map(titleWord).join(" ");
}

/** Initials for avatars / logo text (from name, else email). */
function getAvatarInitials(name, email) {
  const n = String(name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    return (n.slice(0, 2) || "?").toUpperCase();
  }
  const e = String(email || "").trim();
  if (e) return e.slice(0, 2).toUpperCase();
  return "?";
}

const NS_AVATAR_COLORS = Object.freeze([
  { bg: "#eeedfe", text: "#534ab7" },
  { bg: "#e6f1fb", text: "#185fa5" },
  { bg: "#e1f5ee", text: "#0f6e56" },
  { bg: "#faeeda", text: "#854f0b" },
  { bg: "#fbeaf0", text: "#993556" },
  { bg: "#faece7", text: "#993c1d" },
]);

/** Stable avatar/logo color for a seed (same seed → same color). */
function nsAvatarColor(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return NS_AVATAR_COLORS[h % NS_AVATAR_COLORS.length];
}

/**
 * `whatsapp_accounts.usage_type` — one of each per app.
 * 1 Chat (Inbox), 2 Marketing (bulk / UTILITY), 3 Authentication (OTP templates).
 */
const WHATSAPP_USAGE_TYPE_CODES = Object.freeze([
  { value: 1, label: "Chat" },
  { value: 2, label: "Marketing" },
  { value: 3, label: "Authentication" },
]);

/** Meta template status filter (Templates list). Blank = all. */
const WA_TEMPLATE_STATUS_FILTER_OPTIONS = Object.freeze([
  { value: "", label: "All" },
  { value: "APPROVED", label: "APPROVED" },
  { value: "REJECTED", label: "REJECTED" },
  { value: "PENDING", label: "PENDING" },
  { value: "PAUSED", label: "PAUSED" },
]);

/** Meta template category filter (Templates list). Blank = all. */
const WA_TEMPLATE_CATEGORY_FILTER_OPTIONS = Object.freeze([
  { value: "", label: "All" },
  { value: "MARKETING", label: "MARKETING" },
  { value: "UTILITY", label: "UTILITY" },
  { value: "AUTHENTICATION", label: "AUTHENTICATION" },
]);

/**
 * Template create CTA buttons (one list for dropdown + Select).
 * Local `value` is INT; Meta create payload must use `apiType` string.
 */
const WA_TEMPLATE_BUTTON_TYPES = Object.freeze([
  { value: 1, label: "Custom", apiType: "QUICK_REPLY", defaults: { text: "" } },
  {
    value: 2,
    label: "Visit website",
    apiType: "URL",
    defaults: { text: "Visit website", urlType: "static", url: "", example: "" },
  },
  {
    value: 3,
    label: "Call Phone Number",
    apiType: "PHONE_NUMBER",
    defaults: { text: "Call phone number", phone_number: "" },
  },
  { value: 4, label: "Copy offer code", apiType: "COPY_CODE", defaults: { example: "" } },
]);

/**
 * WhatsApp AUTHENTICATION OTP template field defaults (create form).
 * Prefer Zero-tap for silent autofill into the Android app.
 * Package / hash / labels stay blank — enter manually at create time.
 */
const WA_AUTH_OTP_DEFAULTS = Object.freeze({
  otpType: "ZERO_TAP",
  packageName: "",
  signatureHash: "",
  copyButtonText: "",
  autofillText: "",
  messageTtlSeconds: "",
  codeExpirationMinutes: "",
  addSecurityRecommendation: true,
});

/** Meta template review status (string) → same pill badges as the rest of the app. */
function getWhatsAppTemplateStatusDisplay(status) {
  const key = String(status || "").trim().toUpperCase();
  const map = {
    APPROVED: { label: "Approved", badgeClass: "badge rounded-pill bg-light-success" },
    PENDING: { label: "Pending review", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
    IN_APPEAL: { label: "In appeal", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
    REJECTED: { label: "Rejected", badgeClass: "badge rounded-pill bg-light-danger" },
    PAUSED: { label: "Paused", badgeClass: "badge rounded-pill bg-light-secondary" },
    DISABLED: { label: "Disabled", badgeClass: "badge rounded-pill bg-light-secondary" },
    FLAGGED: { label: "Flagged", badgeClass: "badge rounded-pill bg-light-danger" },
    UNKNOWN: { label: "Unknown", badgeClass: "badge rounded-pill bg-light-secondary" },
  };
  return map[key] || map.UNKNOWN;
}

/** Phone number `quality_rating` (WhatsApp → Phone numbers list). */
function getWhatsAppQualityDisplay(rating) {
  const key = String(rating || "").trim().toUpperCase();
  const map = {
    GREEN: { label: "High", badgeClass: "badge rounded-pill bg-light-success" },
    YELLOW: { label: "Medium", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
    RED: { label: "Low", badgeClass: "badge rounded-pill bg-light-danger" },
    UNKNOWN: { label: "Pending", badgeClass: "badge rounded-pill bg-light-secondary" },
  };
  return map[key] || map.UNKNOWN;
}

/** Meta template category → same pill badges as template status. */
function getWhatsAppTemplateCategoryDisplay(category) {
  const key = String(category || "").trim().toUpperCase();
  const map = {
    MARKETING: { label: "Marketing", badgeClass: "badge rounded-pill bg-light-primary" },
    UTILITY: { label: "Utility", badgeClass: "badge rounded-pill bg-light-success" },
    AUTHENTICATION: { label: "Authentication", badgeClass: "badge rounded-pill bg-light-warning text-dark" },
    UNKNOWN: { label: "Unknown", badgeClass: "badge rounded-pill bg-light-secondary" },
  };
  return map[key] || map.UNKNOWN;
}

/**
 * Wall-clock parts of `date` in IANA `timeZone` (via Intl).
 * Shared by backend Engage dates, IST logs, etc.
 */
function getDatePartsInTimeZone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = formatter.formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/** YYYY-MM-DD HH:mm:ss IST (Asia/Kolkata). */
function formatIndiaDateTime(date = new Date()) {
  const p = getDatePartsInTimeZone(date, "Asia/Kolkata");
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)} IST`;
}

/** CSV text + safe filename. `rows` = array of arrays aligned with `headers`. */
function buildCsvReport({ headers = [], rows = [], filename = "export.csv" } = {}) {
  const cell = (value) => {
    const s = value == null ? "" : String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.map(cell).join(","),
    ...rows.map((row) => (Array.isArray(row) ? row : []).map(cell).join(",")),
  ];
  let name = String(filename || "export.csv").replace(/[/\\?%*:|"<>]/g, "_").trim() || "export.csv";
  if (!/\.csv$/i.test(name)) name += ".csv";
  return { csv: `\uFEFF${lines.join("\n")}`, filename: name };
}

const DEFAULT_DISPLAY_LOCALE = "en-IN";

function toDisplayDate(dateInput) {
  if (dateInput == null || dateInput === "") return null;
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }
  const s = String(dateInput).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }
  const parsed = new Date(dateInput);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Display date (e.g. 01 Jun 2026). Pass `timeZone` in options when needed. */
function formatDate(dateInput, options = {}) {
  const date = toDisplayDate(dateInput);
  if (!date) return "";
  const { locale = DEFAULT_DISPLAY_LOCALE, ...intl } = options;
  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...intl,
  });
}

/** Display time (e.g. 4:10 PM). Pass `seconds: true` for seconds. */
function formatTime(dateInput, options = {}) {
  const withSeconds = process.env.REACT_APP_WITH_SECONDS_IN_DATE_TIME === "true";
  const date = toDisplayDate(dateInput);
  if (!date) return "";
  const { locale = DEFAULT_DISPLAY_LOCALE, seconds = false, ...intl } = options;
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...((seconds === true || withSeconds) ? { second: "2-digit" } : {}),
    ...intl,
  });
}

/** Display date + time (e.g. 09 Sep 2026, 3:13 PM). Pass `seconds: true` for seconds. */
function formatDateTime(dateInput, options = {}) {
  const withSeconds = process.env.REACT_APP_WITH_SECONDS_IN_DATE_TIME === "true";
  const { separator = ", ", seconds = false, ...rest } = options;
  const d = formatDate(dateInput, rest);
  const t = formatTime(dateInput, { ...rest, seconds: seconds || withSeconds });
  if (!d && !t) return "";
  if (!t) return d;
  if (!d) return t;
  return `${d}${separator}${t}`;
}

/** Relative time (e.g. 5 minutes ago); older than 30 days uses `formatDate`. */
function formatRelativeDate(dateInput) {
  const date = toDisplayDate(dateInput);
  if (!date) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return formatDate(dateInput);
}

/** Build `https://wa.me/<digits>` from a display phone or raw digits. */
function waMeUrl(displayOrDigits) {
  const d = String(displayOrDigits || "").replace(/\D/g, "");
  return d ? `https://wa.me/${d}` : null;
}

/**
 * WhatsApp markdown (*bold*, _italic_, ~strike~, ```mono```) → safe HTML (escape first).
 * UI wraps with dangerouslySetInnerHTML.
 */
function formatWhatsAppMarkdownHtml(text) {
  const raw = text == null ? "" : String(text);
  if (!raw) return "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/```([^`\n]+)```/g, "<code>$1</code>")
    .replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>")
    .replace(/_([^_\n]+)_/g, "<em>$1</em>")
    .replace(/~([^~\n]+)~/g, "<s>$1</s>");
}

/**
 * Dashboard section toggles — set enabled: false to hide UI and skip backend queries.
 * Charts follow their parent section (delivery → notifications, engage → engage chart, etc.).
 */
const SUBSCRIPTION_DEVICE_OS_APP = Object.freeze(["Android", "iOS"]);
const SUBSCRIPTION_DEVICE_OS_WEB = Object.freeze(["Web"]);

/** type: `app` | `web`. No column → values array (Sequelize). With column → SQL IN fragment. */
function subscriptionDeviceOs(type = "app", column = null) {
  const values = type === "web" ? SUBSCRIPTION_DEVICE_OS_WEB : SUBSCRIPTION_DEVICE_OS_APP;
  if (column == null || column === "") return values;
  const list = values.map((v) => `'${String(v).replace(/'/g, "''")}'`).join(",");
  return `${column} IN (${list})`;
}

const DASHBOARD_SECTIONS = Object.freeze([
  { id: "audience", enabled: true, label: "Audience" },
  { id: "whatsapp", enabled: false, label: "WhatsApp" },
  { id: "engage", enabled: true, label: "Engage" },
  { id: "delivery", enabled: true, label: "Delivery" },
  { id: "support", enabled: true, label: "Customer Support" },
]);

function isDashboardSectionEnabled(sectionId) {
  const row = DASHBOARD_SECTIONS.find((s) => s.id === sectionId);
  return Boolean(row?.enabled);
}

function getEnabledDashboardSectionIds() {
  return DASHBOARD_SECTIONS.filter((s) => s.enabled).map((s) => s.id);
}

const commonUtils = {
  safeJsonParse,
  labelizeKey,
  normalizeSupportFieldNameKey,
  getSupportContextScalar,
  SUBSCRIPTION_STATUS_OPTIONS,
  subscriptionStatusCodes,
  getSubscriptionDisplayStatus,
  METRIC_TYPE_CODES,
  BILLING_CURRENCY_CODES,
  DEFAULT_BILLING_CURRENCY,
  bigAmountFormat,
  countFormat,
  formatMetricPrice,
  BILLING_STATUS_CODES,
  isBillingStatus,
  getCurrentBillingStatusValues,
  isSwitchedBillingStatus,
  CARD_AFA_THRESHOLD_INR,
  HIGH_AFA_UNLOCK_AMOUNT,
  PAYMENT_MODE_CODES,
  PAYMENT_METHOD_CODES,
  PLAN_TYPE_CODES,
  INVOICE_STATUS_CODES,
  INVOICE_KIND_CODES,
  USAGE_SOURCE_CODES,
  PLAN_CHANGE_REQUEST_STATUS_CODES,
  findOptionByValue,
  billingStatusLabel,
  getBillingStatusDisplay,
  getInvoiceStatusDisplay,
  getInvoiceKindDisplay,
  parseInvoiceBreakdown,
  isCheckoutInvoice,
  isCheckoutInvoiceBreakdown,
  getInvoiceMetricRows,
  buildInvoiceChargeLineRows,
  resolveInvoiceTaxSnapshot,
  getPlanChangeRequestStatusDisplay,
  slugify,
  getCurrencyObject,
  calculateGrowthPlanEstimate,
  parseRequestHeader,
  ANNUAL_BILLING_DISCOUNT_FACTOR,
  AUTOMATION_STATUS_CODES,
  AUTOMATION_TEMPLATE_CODES,
  AUTOMATION_ENTRY_TYPE_CODES,
  AUTOMATION_EXIT_TYPE_CODES,
  AUTOMATION_EXIT_CONDITION_BITS,
  AUTOMATION_REENTRY_TYPE_CODES,
  AUTOMATION_GOAL_METRIC_CODES,
  AUTOMATION_MESSAGE_GOAL_METRIC_GROUPS,
  AUTOMATION_SCHEDULE_START_CODES,
  AUTOMATION_SCHEDULE_END_CODES,
  AUTOMATION_NODE_TYPE_CODES,
  AUTOMATION_ENROLL_STATUS_CODES,
  AUTOMATION_EXIT_REASON_CODES,
  AUTOMATION_WAIT_UNIT_CODES,
  getAutomationNodeType,
  serializeAutomationGraph,
  API_KEY_STATUS,
  getApiKeyDisplayStatus,
  PARTNER_TYPE,
  PARTNER_CREDENTIAL_FIELD_TYPE,
  PARTNER_HTTP_METHOD,
  PARTNER_SETTING_KEY,
  PARTNER_USERS_FROM,
  PARTNER_IN_APP_WINDOW_UNIT,
  PARTNER_INCLUDE_VALUES,
  getPartnerTypeLabel,
  getPartnerUsersFromLabel,
  missingRequiredPartnerConfigFields,
  SENT_MESSAGE_DELIVERY_STATUS,
  NOTIFICATION_SEND_JOB_STATUS,
  WHATSAPP_MESSAGE_STATUS,
  WHATSAPP_CAMPAIGN_STATUS,
  SCHEDULED_MESSAGE_STATUS,
  MESSAGE_CREATED_BY_OPTIONS,
  MESSAGE_AUDIENCE_OPTIONS,
  MESSAGE_SEND_KIND_OPTIONS,
  EVENT_METRIC_OPTION,
  EVENT_METRIC_PREFIX,
  FUNNEL_STEP_SEPARATOR,
  FUNNEL_MIN_STEPS,
  FUNNEL_MAX_STEPS,
  FUNNEL_USERS_PAGE_SIZE,
  FUNNEL_USERS_PAGE_MAX,
  FUNNEL_SEGMENT_USER_CAP,
  FUNNEL_SEGMENT_RULE_ID,
  FUNNEL_BREAKDOWN_MAX_GROUPS,
  FUNNEL_BREAKDOWN_OPTIONS,
  FUNNEL_SOURCE_OPTIONS,
  FUNNEL_WINDOW_OPTIONS,
  FUNNEL_FILTER_KEYS,
  FUNNEL_MODE_CODES,
  FUNNEL_EVENT_COLORS,
  CHART_AXIS_STEP_MULTIPLIERS,
  SUPPORT_FIELD_TYPE_OPTIONS,
  SUPPORT_TICKET_FILE_TYPES,
  SUPPORT_TICKET_ALLOWED_FILE_TYPES,
  buildSupportTicketFileAcceptAttribute,
  isSupportTicketFileAllowed,
  APP_SUPPORT_UPLOADS_PATH,
  uploadsPathToFilesystemRelative,
  buildUploadRelativeUrl,
  escapeSupportPlainHtmlText,
  plainSupportTextToHtml,
  normalizeFileName,
  getFileExtension,
  isImageExt,
  isPdfExt,
  inferMimeFromLabel,
  resolveFileKind,
  buildUploadAbsoluteUrl,
  extractFilesFromText,
  DEFAULT_STATUS,
  getStatusDisplay,
  APP_STAFF_STATUS,
  getStaffStatusDisplay,
  APP_SUPPORT_CONVERSATION_STATUS,
  APP_SUPPORT_CONVERSATION_SOURCE,
  APP_SUPPORT_RECORD_ORIGIN,
  APP_SUPPORT_MESSAGE_SENDER_TYPE,
  APP_SUPPORT_MESSAGE_SOURCE_CHANNEL,
  normalizeAppSupportConversationStatus,
  isAppSupportConversationClosed,
  getAppSupportConversationStatusDisplay,
  normalizeAppSupportConversationSource,
  getAppSupportConversationSourceDisplay,
  normalizeAppSupportRecordOrigin,
  getAppSupportRecordOriginDisplay,
  normalizeAppSupportMessageSenderType,
  getAppSupportMessageSenderDisplay,
  normalizeAppSupportMessageSourceChannel,
  generateRandomString,
  USER_CLIENT_SENSITIVE_KEYS,
  userDataToStorageString,
  getPlanDisplayName,
  ONELINK_MODAL_STEP,
  ONELINK_TEMPLATE,
  ONELINK_CAMPAIGN_TYPE,
  ONELINK_MEDIA_SOURCE,
  ONELINK_LINK_STATUS,
  ONELINK_LINK_STATUS_ACTIVE,
  ONELINK_SOCIAL_PREVIEW,
  ONELINK_REDIRECT_TYPE,
  ONELINK_DEFAULTS,
  onelinkNormalizeOptionValue,
  getCampaignTypeLabel,
  getMediaSourceLabel,
  getOnelinkTemplateEntry,
  getOnelinkSocialPreviewEntry,
  getOnelinkRedirectLabel,
  getLinkStatusDisplay,
  buildDefaultLinkName,
  parseCustomParams,
  DEFAULT_STATUS_OPTIONS,
  formatPersonDisplayName,
  getAvatarInitials,
  NS_AVATAR_COLORS,
  nsAvatarColor,
  WHATSAPP_USAGE_TYPE_CODES,
  WA_TEMPLATE_STATUS_FILTER_OPTIONS,
  WA_TEMPLATE_CATEGORY_FILTER_OPTIONS,
  WA_TEMPLATE_BUTTON_TYPES,
  WA_AUTH_OTP_DEFAULTS,
  getWhatsAppTemplateStatusDisplay,
  getWhatsAppQualityDisplay,
  getWhatsAppTemplateCategoryDisplay,
  WHATSAPP_TEMPLATE_STATUS_CODES,
  getWhatsAppTemplateStatusCode,
  getWhatsAppTemplateStatusKey,
  WHATSAPP_TEMPLATE_QUALITY_CODES,
  getWhatsAppTemplateQualityCode,
  getWhatsAppTemplateQualityKey,
  WHATSAPP_TEMPLATE_CATEGORY_CODES,
  getWhatsAppTemplateCategoryCode,
  getWhatsAppTemplateCategoryKey,
  WHATSAPP_TEMPLATE_REJECTED_REASON_CODES,
  getWhatsAppTemplateRejectedReasonCode,
  getWhatsAppTemplateRejectedReasonKey,
  waMeUrl,
  formatWhatsAppMarkdownHtml,
  getDatePartsInTimeZone,
  formatIndiaDateTime,
  DEFAULT_DISPLAY_LOCALE,
  toDisplayDate,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeDate,
  buildCsvReport,
  ROLE_TYPES,
  PERMISSION_SCOPES,
  TEAM_MEMBER_ROLE_SLUG,
  isStaffUser,
  roleHasUnrestrictedAccess,
  can,
  canAny,
  canAll,
  SUPERADMIN_PERMISSIONS,
  ADMIN_PERMISSIONS,
  groupAdminPermissionsForUi,
  SYSTEM_ROLES,
  getSystemRoleSlugs,
  getCustomerSupportPermissionKeys,
  DASHBOARD_SECTIONS,
  isDashboardSectionEnabled,
  getEnabledDashboardSectionIds,
  SUBSCRIPTION_DEVICE_OS_APP,
  SUBSCRIPTION_DEVICE_OS_WEB,
  subscriptionDeviceOs,
};

export default commonUtils;
